import { API_DATE_FORMAT } from 'lib/constants';
import {
  calculateAutoId,
  displayAutoId,
  getFieldGroupInfo,
  returnFieldInstanceIdsMatchingSearchTerm,
} from 'lib/fields';
import {
  arrayUnique,
  filterArrayEmpty,
  formatDate,
  getAseResultKey,
  getSearchTermsFromSearchValue,
  numberToDate,
  objectRemoveNull,
  validateEnumFallback,
} from 'lib/riskbase';
import { testCriterion } from 'lib/testCriterion';
import { theme } from 'lib/theme';
import { intersection, isEqual, orderBy, sortBy, toInteger } from 'lodash';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';
import { selectGeneric } from 'store/app/selectors';
import {
  Action as ResultsAction,
  Assessment,
  AssessmentItemTypes,
  AssessmentResults,
  AssessmentState,
  ChecklistItemResult,
  ChecklistItemResultEnum,
  Control as ResultsControl,
  Details,
  ExplorerOptionsProps,
  FindingPreviousParent,
  InstanceParentParams,
  QuestionResult as ResultsQuestion,
  SectionResult as ResultsSection,
  Question as TemplateQuestion,
  Section as TemplateSection,
  ActionSuggestion,
  ControlSuggestion,
  AseTplFieldGroupWithCalculatedInfo,
  AseTplInstanceCheck,
  FieldInstanceCheckScheduleStatusEnum,
  AseTplFieldField,
  AseFieldInstanceWithFormattedAutoId,
} from 'store/assessments/types';
import {
  AssessmentStatus,
  ComplianceSubtype,
  ComplianceType,
} from 'store/organisations/types';
import { PinNewWithName } from 'store/plan/types';
import { State } from 'store/types';
import {
  AssessmentQuestionType,
  FieldRepeatableOrderConfig,
  FieldRepeatableOrderEnum,
  FieldInstanceWithFormattedAutoId,
  FieldResult,
  GENERIC_ASSESSMENT_EXPLORER_OPTIONS,
  createSelectorCached,
  createSelectorCachedResultDeepEqual,
  FieldTypes,
  GenericKeyAseShowSuggestionsUnderChecklistItems,
  EMPTY_ARRAY,
  AssessmentStatusesEnum,
} from 'types/types';
import { selectUserId, selectUserReference } from 'store/auth/selectors';
import { VisibleQuestionResults } from 'store/assets/typesAse';
import { filterByAvailabilityCategory } from 'screens/AssessmentScreen/Explorer/utils';
import {
  defaultExplorerOptions,
  defaultGroupExplorerOptions,
} from './templates';
import { getRepeatableFilterKey } from './utilities';

type AssessmentsStateTree = Omit<Partial<State>, 'assessments'> &
  Pick<State, 'assessments'>;

type SelectInstancesForGroup = {
  groupId: string;
  parent: InstanceParentParams;
  inAssessmentOnly: boolean;
  filtered?: boolean;
};

const forGroupCacheKeyFnc = (_: State, conf: SelectInstancesForGroup) =>
  `${conf.groupId}${
    conf.parent
      ? `-${conf.parent.fieldId}:${conf.parent.instanceId || 0}`
      : '-0:0'
  }${conf.inAssessmentOnly ? '-in' : '-all'}` +
  `${conf.filtered ? '-filtered' : ''}`;

const selectAssessmentState = (
  state: AssessmentsStateTree,
): AssessmentState => {
  if (!state.assessments) {
    // eslint-disable-next-line no-console
    console.info(
      'Trying to access assessment state before assessment is loaded',
    );
  }
  return state.assessments!;
};

// refactor out into function

export const selectAseIsOpen = createSelector(
  selectAssessmentState,
  (state) => state.isOpen,
);

const selectAse = createSelector(
  selectAssessmentState,
  (assessmentsState): Required<AssessmentState>['assessment'] => {
    if (!assessmentsState.assessment) {
      // eslint-disable-next-line no-console
      console.info(
        'Trying to access assessment state before assessment is loaded',
      );
    }
    return assessmentsState.assessment!;
  },
);

export const selectAseContext = createSelector(
  selectAssessmentState,
  (aseState) => aseState.context,
);

export const selectAseIsSectionCollapsed = createCachedSelector(
  [selectAssessmentState, (_: State, sectionId: string) => sectionId],
  (assessment, sectionId): boolean => !!assessment.collapsedSections[sectionId],
)((_: State, sectionId: string) => sectionId);

export const selectAseIsOnline = createSelector(
  selectAse,
  (ase) => ase.isOnline,
);

export const selectAseId = createSelector(
  selectAse,
  (assessment) => assessment.id,
);

export const selectAseAssetId = createSelector(
  selectAse,
  (assessment) => assessment.assetId,
);

export const selectAseFeatures = createSelector(
  selectAse,
  (assessment) => assessment.features,
);

export const selectAseFeatureDownloadBundle = createSelector(
  selectAseFeatures,
  (features) => features.downloadBundle,
);

export const selectAseOrganisationId = createSelector(
  selectAse,
  (assessment) => assessment.organisationId,
);

export const selectAseTask = createSelector(
  selectAse,
  (assessment) => assessment.task,
);

export const selectAseTemplate = createSelector(
  selectAse,
  (assessment) => assessment.template,
);

export const selectAseTemplateSettings = createSelector(
  selectAseTemplate,
  (template) => template.settings || {},
);

const selectAseTemplateFields = createSelector(
  selectAseTemplate,
  (template): Required<Assessment['template']>['fields'] => template.fields,
);

export const selectAseResults = createSelector(
  selectAse,
  (assessment): AssessmentResults => assessment.results,
);

const selectAseAssetData = createSelector(
  selectAse,
  (assessment) => assessment.assetData,
);

export const selectAseAssetAssessmentTimestamps = createSelector(
  selectAseAssetData,
  (assetData) => assetData.assessmentTimestamps,
);

export const selectAseAssetRiskRatings = createSelector(
  selectAseAssetData,
  (assetData) => assetData.riskRatings,
);

export const selectAseAssetPlans = createSelector(
  selectAseAssetData,
  (assetData) => assetData.plans,
);

export const selectAseAssetPlan = createCachedSelector(
  selectAseAssetPlans,
  (_: State, planId: string) => planId,
  (plans, planId) => plans[planId],
)((_: State, planId: string) => planId);

export const selectAseAssetTeamResponsibility = createSelector(
  selectAseAssetData,
  (assetData) => assetData.teamResponsibility,
);

export const selectAseAssetDetails = createSelector(
  selectAse,
  (assessment) => assessment.assetData.details,
);

const selectAseOtherData = createSelector(
  selectAse,
  (assessment) => assessment.otherData,
);

export const selectAseAllowedAuthorOrganisations = createSelector(
  selectAseOtherData,
  (otherData) => otherData.allowedAuthorOrganisations,
);

export const selectAseAllowedAuthors = createSelector(
  selectAseOtherData,
  (otherData) => otherData.allowedAuthors,
);

const selectAseOtherDataFields = createSelector(
  selectAseOtherData,
  (otherData) => otherData.fields,
);

const selectHidePreviousFindings = createSelector(
  selectAseTemplate,
  (template): boolean => !!template.hidePreviousFindings,
);

const selectPreviousActionsLimitComplianceTypes = createSelector(
  selectAseTemplate,
  (template) => template.previousActionsLimitComplianceTypes,
);

const selectPreviousControlsLimitComplianceTypes = createSelector(
  selectAseTemplate,
  (template) => template.previousControlsLimitComplianceTypes,
);

export const selectAseTplFlags = createSelector(
  selectAseTemplate,
  (template: Assessment['template']) => template.flags,
);

export const selectAseTplHasFlags = createSelector(
  selectAseTplFlags,
  (flags) => !!flags.length,
);

export const selectAseTplFlag = createSelectorCached(
  [selectAseTplFlags, (_: State, flagId: string) => flagId],
  (flags, flagId) => flags.find((f) => f.id === flagId),
);

export const selectAseTplSignatories = createSelector(
  selectAseOtherData,
  (data: Assessment['otherData']) => data.signatories,
);

export const selectAseTplHasSignatories = createSelector(
  selectAseTplSignatories,
  (signatories) => !!signatories.length,
);

export const selectAseTplSignatory = createSelectorCached(
  [selectAseTplSignatories, (_: State, signatoryId: string) => signatoryId],
  (signatories, signatoryId) => signatories.find((f) => f.id === signatoryId),
);

export const selectAseTplSections = createSelector(
  selectAseTemplate,
  (template: Assessment['template']) => template.sections,
);

export const selectAseTplSectionIds = createSelector(
  selectAseTemplate,
  (template: Assessment['template']) => template.section_ids,
);

const selectAseTplRegularQuestionIds = createSelector(
  selectAseTplSections,
  (sections): string[] =>
    Object.values(sections).reduce<string[]>((acc, section) => {
      acc.push(...section.question_ids);
      return acc;
    }, []),
);

export const selectAseTplSectionQuestionIds = createCachedSelector(
  selectAseTplSections,
  (_: AssessmentsStateTree, id: string) => id,
  (sections, id): string[] => sections[id]?.question_ids || [],
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseTplAnswers = createSelector(
  selectAseTemplate,
  (template) => template.answers,
);

export const selectAseTplAnswer = createCachedSelector(
  selectAseTplAnswers,
  (_: State, answerId: string) => answerId,
  (answers, answerId) => answers[answerId],
)((_, answerId) => answerId);

export const selectAseTplSection = createCachedSelector(
  selectAseTemplate,
  (_: AssessmentsStateTree, id: string) => id,
  (assessmentTemplate: Assessment['template'], id: string): TemplateSection =>
    assessmentTemplate.sections[id]!,
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseTplQuestions = createSelector(
  selectAseTemplate,
  (template: Assessment['template']): Record<string, TemplateQuestion> =>
    template.questions,
);

const selectAseTplFieldInstanceChecks = createSelector(
  selectAseTemplate,
  (template): Record<string, AseTplInstanceCheck> =>
    template.fieldInstanceChecks,
);

export const selectAseTplFieldInstanceCheck = createCachedSelector(
  selectAseTplFieldInstanceChecks,
  (_: State, checkId: string) => checkId,
  (checks, checkId): AseTplInstanceCheck | undefined => checks[checkId],
)((_: AssessmentsStateTree, checkId: string) => checkId);

const selectAseResultFieldInstanceChecks = createSelector(
  selectAseResults,
  (results) => results.fieldInstanceChecks,
);
export const selectAseResultFieldInstanceCheck = createCachedSelector(
  selectAseResultFieldInstanceChecks,
  (_: State, checkId: string, fieldInstanceId: string) =>
    getAseResultKey(checkId, fieldInstanceId),
  (checkResults, resultKey) => checkResults[resultKey],
)((_: State, checkId: string, fieldInstanceId: string) =>
  getAseResultKey(checkId, fieldInstanceId),
);

export const selectAseTplQuestion = createCachedSelector(
  selectAseTplQuestions,
  (_: AssessmentsStateTree, id: string) => id,
  (questions: Record<string, TemplateQuestion>, id: string): TemplateQuestion =>
    questions[id]!,
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseTplQuestionAnswers = createCachedSelector(
  selectAseTplAnswers,
  selectAseTplQuestion,
  (answers, questionTemplate) =>
    questionTemplate.answer_ids.map((answerIds) => answers[answerIds]!),
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseTplChecklistItems = createSelector(
  selectAseTemplate,
  (template) => template.checklistItems || {},
);

export const selectAseTplAnswersForQuestion = createCachedSelector(
  selectAseTplAnswers,
  selectAseTplQuestion,
  (answers, question) =>
    question.answer_ids.map((answerId) => answers[answerId]!),
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseTplChecklistItemsForQuestion = createCachedSelector(
  selectAseTplChecklistItems,
  selectAseTplQuestion,
  (checklistItems, question) =>
    (question.checklistItemIds || []).map(
      (checklistItemId) => checklistItems[checklistItemId]!,
    ),
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseIsEditable = createSelector(
  selectAseIsOnline,
  selectAseResults,
  selectAseFeatures,
  (isOnline, results, features): boolean =>
    isOnline ? !!features.edit : !!results.editable,
);

export const selectAseExplorerOptions = createSelector(
  (state: State) =>
    selectGeneric(state, GENERIC_ASSESSMENT_EXPLORER_OPTIONS) as
      | ExplorerOptionsProps
      | undefined,
  (options): ExplorerOptionsProps => ({
    ...defaultExplorerOptions,
    ...options,
  }),
);

export const selectAseExplorerOptionFindingReference = createSelector(
  selectAseExplorerOptions,
  (explorerOptions) => !!explorerOptions.findingShowReference,
);

export const selectAseExplorerOptionQuestionDescriptions = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['questionDescriptions'] =>
    explorerOptions.questionDescriptions,
);

export const selectAseExplorerOptionFindingDescriptions = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['findingDescriptions'] =>
    explorerOptions.findingDescriptions,
);
export const selectAseExplorerOptionFindingSuggestionDescriptions =
  createSelector(
    selectAseExplorerOptions,
    (explorerOptions): ExplorerOptionsProps['findingSuggestionDescriptions'] =>
      explorerOptions.findingSuggestionDescriptions,
  );

export const selectAseExplorerOptionFindingAdditional = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['findingAdditional'] =>
    explorerOptions.findingAdditional,
);

export const selectAseExplorerOptionFindingSuggestionAdditional =
  createSelector(
    selectAseExplorerOptions,
    (explorerOptions): ExplorerOptionsProps['findingSuggestionAdditional'] =>
      explorerOptions.findingSuggestionAdditional,
  );

export const selectAseExplorerOptionSize = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['size'] => explorerOptions.size,
);

export const selectAseExplorerOptionCollapseFinding = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['findingsCollapse'] =>
    explorerOptions.findingsCollapse,
);

const selectAseExplorerOptionFindingsUnreviewed = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['findingsUnreviewed'] =>
    explorerOptions.findingsUnreviewed,
);

const selectAseExplorerOptionFindingsNewReviewed = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['findingsNewReviewed'] =>
    explorerOptions.findingsNewReviewed,
);

export const selectAseExplorerOptionQuestionsNegative = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['questionsNegative'] =>
    explorerOptions.questionsNegative,
);

export const selectAseExplorerOptionQuestionsPositive = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['questionsPositive'] =>
    explorerOptions.questionsPositive,
);

export const selectAseExplorerOptionQuestionsUnanswered = createSelector(
  selectAseExplorerOptions,
  (explorerOptions): ExplorerOptionsProps['questionsUnanswered'] =>
    explorerOptions.questionsUnanswered,
);

const selectAseExplorerOptionShowValidationErrorsTemporary = createSelector(
  selectAse,
  (assessment) => assessment.showValidationErrors,
);

export const selectAseExplorerOptionShowValidationErrors = createSelector(
  selectAseExplorerOptions,
  selectAseExplorerOptionShowValidationErrorsTemporary,
  selectAseIsEditable,
  (
    explorerOptions,
    temporaryValue,
    isEditable,
  ): ExplorerOptionsProps['showValidationErrors'] =>
    (explorerOptions.showValidationErrors || temporaryValue) && isEditable,
);

export const selectAseDataFieldGroups = createSelector(
  selectAseOtherDataFields,
  (data) => data.groups,
);

export const selectAseDataFieldFields = createSelector(
  selectAseOtherDataFields,
  (data) => data.fields,
);

const selectAseDataFieldFieldIdsWhereTypeIsRepeatable = createSelector(
  selectAseDataFieldFields,
  (fields) =>
    Object.values(fields).reduce<string[]>((acc, field) => {
      if (field.type === FieldTypes.GROUP) {
        acc.push(field.id);
      }
      return acc;
    }, []),
);

const selectAseDataFieldFieldsToGroups = createSelector(
  selectAseDataFieldGroups,
  (groups) =>
    Object.values(groups).reduce<Record<string, string>>((acc, group) => {
      group.fieldIds.forEach((fieldId) => {
        acc[fieldId] = group.id;
      });
      return acc;
    }, {}),
);

export const selectAseFilesAst = createSelector(
  selectAseAssetData,
  (assetData) => assetData.files,
);

export const selectAseFieldResultsAst = createSelector(
  selectAseAssetData,
  (assetData) => assetData.fieldResults,
);

const selectAseFieldInstancesAstRaw = createSelector(
  selectAseAssetData,
  (assetData) => assetData.fieldInstances,
);

export const selectAseFieldInstancesAst = createSelector(
  selectAseDataFieldGroups,
  selectAseDataFieldFields,
  selectAseFieldInstancesAstRaw,
  (groups, fields, instances) =>
    Object.entries(instances).reduce<
      Record<string, AseFieldInstanceWithFormattedAutoId>
    >((acc, [instanceId, instance]) => {
      const group = groups[instance.groupId];
      if (
        group &&
        instance.action !== 'delete' &&
        instance.action !== 'remove'
      ) {
        acc[instanceId] = {
          ...instance,
          action: instance.action,
          results: Object.entries(instance.results).reduce<
            Record<string, FieldResult>
          >((acc1, [fieldId, result]) => {
            const field = fields[fieldId];
            if (field && result.action !== 'delete') {
              acc1[fieldId] = result;
            }
            return acc1;
          }, {}),
          formattedAutoId: displayAutoId(
            instance.autoId!,
            group.autoIdZeroFill || 0,
            group.autoIdPrefix || '',
          ),
          inAssessment: false,
        };
      }
      return acc;
    }, {}),
);

export const selectAseFieldInstanceAst = createCachedSelector(
  selectAseFieldInstancesAst,
  (_: State, instanceId: string) => instanceId,
  (instances, instanceId) => instances[instanceId],
)((_: State, instanceId: string) => instanceId);

const selectAseActionsAst = createSelector(
  selectAseAssetData,
  selectHidePreviousFindings,
  selectPreviousActionsLimitComplianceTypes,
  (assetData, shouldHide, limitComplianceTypes): ResultsAction[] => {
    if (shouldHide) {
      return [];
    }
    if (limitComplianceTypes) {
      // @REMOVE || '-1' after 2022-03-01 – at introduction actions didn't have a complianceTypeId
      return limitComplianceTypes.length
        ? assetData.actions.filter((finding) =>
            limitComplianceTypes.includes(finding.complianceTypeId || '-1'),
          )
        : [];
    }
    return assetData.actions;
  },
);

const selectAseActionAst = createCachedSelector(
  (_: State, id: string) => id,
  selectAseActionsAst,
  (actionId, actions) => actions.find((action) => action.id === actionId),
)((_: State, id: string) => id);

const selectAseControlsAst = createSelector(
  selectAseAssetData,
  selectHidePreviousFindings,
  selectPreviousControlsLimitComplianceTypes,
  selectAseAssetId,
  (assetData, shouldHide, limitComplianceTypes): ResultsControl[] => {
    if (shouldHide) {
      return [];
    }
    if (limitComplianceTypes) {
      // @REMOVE || '-1' after 2022-03-01 – at introduction controls didn't have a complianceTypeId
      return limitComplianceTypes.length
        ? assetData.controls.filter((finding) =>
            limitComplianceTypes.includes(finding.complianceTypeId || '-1'),
          )
        : [];
    }
    return assetData.controls;
  },
);

const selectAseControlAst = createCachedSelector(
  (_: State, id: string) => id,
  selectAseControlsAst,
  (controlId, controls) => controls.find((control) => control.id === controlId),
)((_: State, id: string) => id);

export const selectAseTeams = createSelector(
  selectAseOtherData,
  (data) => data.teams,
);

export const selectAseAccounts = createSelector(
  selectAseOtherData,
  (data) => data.accounts,
);

export const selectAseDueTypes = createSelector(
  selectAseOtherData,
  (data) => data.due_types,
);

export const selectAseCategories = createSelector(
  selectAseOtherData,
  (data) => data.categories,
);

export const selectAseTaskStatuses = createSelector(
  selectAseOtherData,
  (data) => data.task_statuses,
);

export const selectAseControlStatuses = createSelector(
  selectAseOtherData,
  (data) => data.control_statuses,
);

export const selectAseTimescales = createSelector(
  selectAseOtherData,
  (data) => data.priorities,
);

export const selectAseSeverities = createSelector(
  selectAseOtherData,
  (data) => data.secondary_priorities,
);

const selectAseComplianceTypes = createSelector(
  selectAseOtherData,
  (data) => data.compliance_types,
);

const selectAseComplianceSubtypes = createSelector(
  selectAseOtherData,
  (data) => data.compliance_sub_types,
);

const selectAseStatuses = createSelector(
  selectAseOtherData,
  (data) => data.assessment_statuses,
);

export const selectAseQuestionPreviousAnswer = createCachedSelector(
  selectAseAssetData,
  (
    _: AssessmentsStateTree,
    questionId: string,
    fieldInstanceId: string | null,
  ) => getAseResultKey(questionId, fieldInstanceId),
  (assetData, resultKey) => assetData.answers[resultKey],
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseReference = createSelector(
  selectAseResults,
  (results) => results.reference,
);

const selectAseFieldInstanceNextAutoIds = createSelector(
  selectAseAssetData,
  (assetData): Record<string, number> =>
    Object.values(assetData.fieldInstances).reduce<Record<string, number>>(
      (acc, { groupId, autoId }) => {
        const groupAutoId = acc[groupId] || 1;
        acc[groupId] =
          autoId && groupAutoId < autoId + 1 ? autoId + 1 : groupAutoId;
        return acc;
      },
      { ...assetData.fieldInstanceAutoIds },
    ),
);

const selectAseFieldResultsAseRaw = createSelector(
  selectAseResults,
  (results) => results.fieldResults,
);
const selectAseFieldInstancesAseRaw = createSelector(
  selectAseResults,
  (results) => results.fieldInstances,
);

export const selectAseFieldResultsAse = createSelector(
  selectAseFieldResultsAseRaw,
  (assessmentResults): Record<string, FieldResult> =>
    Object.entries(assessmentResults).reduce<Record<string, FieldResult>>(
      (acc, [fieldId, result]) => {
        if (
          result.value !== null &&
          result.action !== 'delete' &&
          result.action !== 'remove'
        ) {
          acc[fieldId] = result as FieldResult;
        }
        return acc;
      },
      {},
    ),
);

export const selectAseFieldResultsAstAse = createSelector(
  selectAseFieldResultsAst,
  selectAseFieldResultsAse,
  (resultsAsset, resultAssessment) => ({
    ...resultsAsset,
    ...resultAssessment,
  }),
);

const selectAseFieldInstancesAseInstanceIdToGroupId = createSelector(
  selectAseFieldInstancesAseRaw,
  (instances) =>
    Object.values(instances).reduce<Record<string, string>>(
      (acc, { instanceId, groupId, action }) => {
        if (action !== 'delete' && action !== 'remove') {
          acc[instanceId] = groupId;
        }
        return acc;
      },
      {},
    ),
);

/**
 * Select instances included in the assessment. Don't include instances
 * that are not included in the assessment. Results are from assessment
 * only - asset results are not included
 */
export const selectAseFieldInstancesAse = createSelector(
  [
    selectAseDataFieldGroups,
    selectAseDataFieldFields,
    selectAseFieldInstanceNextAutoIds,
    selectAseFieldInstancesAstRaw,
    selectAseFieldInstancesAseRaw,
  ],
  (groups, fields, nextAutoIds, assetInstances, aseInstances) =>
    Object.entries(aseInstances).reduce<
      Record<string, AseFieldInstanceWithFormattedAutoId>
    >((acc, [instanceId, aseInstance]) => {
      const assetInstance = assetInstances[instanceId];
      if (
        // Include if it isn't deleted
        aseInstance.action !== 'delete' &&
        aseInstance.action !== 'remove' &&
        // And where it's new OR it exists on asset
        (!aseInstance.autoId ||
          (assetInstance &&
            assetInstance.action !== 'delete' &&
            assetInstance.action !== 'remove'))
      ) {
        const group = groups[aseInstance.groupId];
        if (group) {
          acc[instanceId] = {
            ...(assetInstance || aseInstance),
            action: aseInstance.action,
            results: Object.entries(aseInstance.results).reduce<
              Record<string, FieldResult>
            >((acc1, [fieldId, result]) => {
              const field = fields[fieldId];
              if (field && result.action !== 'delete') {
                acc1[fieldId] = result;
              }
              return acc1;
            }, {}),
            updatedTs: Math.max(
              assetInstance?.updatedTs || 0,
              aseInstance.updatedTs,
            ),
            formattedAutoId: assetInstance
              ? displayAutoId(
                  assetInstance.autoId!,
                  group.autoIdZeroFill || 0,
                  group.autoIdPrefix || '',
                )
              : displayAutoId(
                  calculateAutoId(
                    aseInstance.autoId,
                    aseInstance.draftAutoId,
                    nextAutoIds[aseInstance.groupId] || 1,
                  ),
                  group.autoIdZeroFill || 0,
                  group.autoIdPrefix || '',
                ),
            inAssessment: true,
          };
        }
      }
      return acc;
    }, {}),
);

const selectAseAssetAseRaw = createSelector(
  selectAseResults,
  (results) => results.asset,
);

export const selectAseAssetAse = createSelector(
  selectAseAssetDetails,
  selectAseAssetAseRaw,
  (assetDetails, aseDetails) =>
    assetDetails.parentName
      ? // Address is not-editable if controlled by parent
        Object.fromEntries(
          Object.entries(aseDetails).filter(([key]) => {
            return ![
              'address1',
              'address2',
              'city',
              'county',
              'postcode',
            ].includes(key);
          }),
        )
      : aseDetails,
);

export const selectAseAssetAstAseMerged = createSelector(
  selectAseAssetDetails,
  selectAseAssetAse,
  (assetDetails, resultAssetDetails) => ({
    ...assetDetails,
    ...objectRemoveNull(resultAssetDetails),
  }),
);

export const selectAseDetails = createSelector(
  selectAseResults,
  (results): Details => results.details,
);

const selectAseDetailsAssessedBy = createSelector(
  selectAseDetails,
  (details): Details['assessedBy'] => details.assessedBy,
);

const selectAseIsAuthor = createSelector(
  [selectAseDetailsAssessedBy, selectUserId, selectUserReference],
  (assessedBy, userId, userReference) =>
    !!assessedBy.find(
      (assessor) => assessor.id === userId || assessor.id === userReference,
    ),
);

export const selectAseActionTimescaleDate = createSelector(
  selectAseDetails,
  (details): string | null => details.actionTimescaleDate || null,
);

export const selectAseActionTimescaleUpdateReviewed = createSelector(
  selectAseDetails,
  (details): boolean => !!details.actionTimescaleUpdateReviewed,
);

export const selectAseStatus = createSelector(
  selectAseStatuses,
  selectAseDetails,
  (statuses, details): AssessmentStatus => statuses[details.statusId]!,
);

export const selectAseCannotEditReason = createSelector(
  [selectAseIsAuthor, selectAseIsEditable, selectAseStatus],
  (
    isAuthor,
    isEditable,
    status,
  ): null | 'AssessorOnline' | 'AssessorSubmitted' | 'NonAssessor' =>
    isAuthor
      ? !isEditable
        ? status.id === AssessmentStatusesEnum.submitted
          ? 'AssessorSubmitted'
          : status.id === AssessmentStatusesEnum.inprogress
            ? 'AssessorOnline'
            : null
        : null
      : !isEditable
        ? status.id === AssessmentStatusesEnum.inprogress
          ? 'NonAssessor'
          : null
        : null,
);

export const selectAseCanChangeStatus = createSelector(
  [selectAseIsEditable, selectAseStatus, selectAseFeatures],
  (isEditable, status, features): boolean =>
    (isEditable && status.id === AssessmentStatusesEnum.inprogress) ||
    features.unsubmit ||
    features.approve ||
    features.unapprove,
);

export const selectAseIsApproved = createSelector(
  selectAseStatus,
  (status): boolean => status.name === 'Approved',
);

export const selectAseComplianceSubtype = createSelector(
  selectAseComplianceSubtypes,
  selectAseTemplate,
  (complianceSubType, template): ComplianceSubtype =>
    complianceSubType[template.complianceSubtypeId]!,
);

export const selectAseComplianceType = createSelector(
  selectAseComplianceTypes,
  selectAseComplianceSubtype,
  (complianceTypes, complianceSubType): ComplianceType =>
    complianceTypes[complianceSubType.compliance_type_id]!,
);

const selectAseSections = createSelector(
  selectAseResults,
  (results): Record<string, ResultsSection | null> => results.sections!,
);

export const selectAseSection = createCachedSelector(
  selectAseSections,
  (_: AssessmentsStateTree, id: string) => id,
  (results, id): ResultsSection | null => results[id] || null,
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseResultQuestions = createSelector(
  selectAseResults,
  (results): Record<string, ResultsQuestion | null> => results.questions!,
);

export const selectAseResultQuestion = createSelectorCached(
  selectAseResults,
  (_: State, questionId: string, fieldInstanceId: string | null) =>
    getAseResultKey(questionId, fieldInstanceId),
  (results, resultKey): ResultsQuestion | null =>
    results.questions[resultKey] || null,
);

export const selectAseResultQuestionAnswerId = createSelectorCached(
  selectAseResults,
  (_: State, questionId: string, fieldInstanceId: string | null) =>
    getAseResultKey(questionId, fieldInstanceId),
  (results, resultKey): ResultsQuestion['answerId'] | null =>
    results.questions[resultKey]?.answerId || null,
);

const selectAseActionsAse = createSelector(selectAseResults, (results) =>
  Object.entries(results.actions).reduce<Record<string, ResultsAction>>(
    (acc, [actionId, action]) => {
      if (action !== null) {
        acc[actionId] = action;
      }
      return acc;
    },
    {},
  ),
);

const selectAseActionIdsAse = createSelector(selectAseActionsAse, (actions) =>
  Object.keys(actions),
);

export const selectAseActionCountAse = createSelector(
  selectAseActionIdsAse,
  (actionIds) => actionIds.length,
);

const selectAseActionAse = createCachedSelector(
  selectAseActionsAse,
  (_: State, id: string) => id,
  (actions, id: string) => actions[id],
)((_: State, id: string) => id);

export const selectAseActionAstAse = createCachedSelector(
  selectAseActionAse,
  selectAseActionAst,
  (aseAction, assetAction) => aseAction || assetAction,
)((_: AssessmentsStateTree, id: string) => id);

/**
 * true = exists in assessment
 * false = does not exist assessment, but does in asset
 * undefined = doesn't exist anywhere
 */
export const selectAseActionInAssessment = createCachedSelector(
  selectAseActionAstAse,
  (action): boolean | undefined =>
    !action
      ? undefined
      : action.sectionId !== null || action.questionId !== null,
)((_: AssessmentsStateTree, id: string) => id);

const selectAseControlsAse = createSelector(selectAseResults, (results) =>
  Object.entries(results.controls).reduce<Record<string, ResultsControl>>(
    (acc, [controlId, control]) => {
      if (control !== null) {
        acc[controlId] = control;
      }
      return acc;
    },
    {},
  ),
);

const selectAseControlIdsAse = createSelector(
  selectAseControlsAse,
  (controls) => Object.keys(controls),
);

export const selectAseControlCountAse = createSelector(
  selectAseControlIdsAse,
  (controlIds) => controlIds.length,
);

const selectAseControlAse = createCachedSelector(
  selectAseControlsAse,
  (_: AssessmentsStateTree, id: string) => id,
  (controls, id: string) => controls[id],
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseControlAstAse = createCachedSelector(
  selectAseControlAse,
  selectAseControlAst,
  (aseControl, assetControl) => aseControl || assetControl,
)((_: AssessmentsStateTree, id: string) => id);

/**
 * true = exists in assessment
 * false = does not exist assessment, but does in asset
 * undefined = doesn't exist anywhere
 */
export const selectAseControlInAssessment = createCachedSelector(
  selectAseControlAstAse,
  (control): boolean | undefined =>
    !control
      ? undefined
      : control.sectionId !== null || control.questionId !== null,
)((_: AssessmentsStateTree, id: string) => id);

const selectAseTplCriteria = createSelector(
  selectAseTemplate,
  (template) => template.criteria,
);

const selectAseAssessedDate = createSelector(
  selectAseDetails,
  (details) => details.assessedDate,
);

const selectAseCriteriaResults = createSelector(
  selectAseFieldResultsAstAse,
  selectAseTplCriteria,
  selectAseAssessedDate,
  selectAseTask,
  (fieldResults, criteria, assessedDate, task): Record<string, boolean> =>
    Object.fromEntries(
      Object.entries(criteria).map(([criteriaId, criteriaSet]) => [
        criteriaId,
        criteriaSet.every((criteriaOr) =>
          criteriaOr.some((criteriaAnd) =>
            criteriaAnd.every((criterion) =>
              testCriterion(criterion, {
                fieldResults,
                variables: {
                  assessed_date: assessedDate,
                  due_date: task?.dueDate
                    ? formatDate(numberToDate(task.dueDate), API_DATE_FORMAT)
                    : '',
                },
              }),
            ),
          ),
        ),
      ]),
    ),
);

export const selectAseResultChecklistItems = createSelector(
  selectAseResults,
  (results) => results.checklistItems,
);

const selectAseResultQuestionComments = createSelector(
  selectAseResults,
  (results) => results.questionComments,
);

const selectAseSuggestedActionsUsage = createSelector(
  selectAseActionsAse,
  (actions) =>
    Object.values(actions).reduce<Record<string, number>>((acc, finding) => {
      if (finding.suggestionId) {
        const key = getAseResultKey(
          finding.suggestionId,
          finding.fieldInstanceId,
        );
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {}),
);

const selectAseTplSuggestedActions = createSelector(
  selectAseTemplate,
  (template: Assessment['template']) => template!.actionSuggestions,
);

export const selectAseTplSuggestedAction = createSelectorCachedResultDeepEqual(
  selectAseTplSuggestedActions,
  selectAseSuggestedActionsUsage,
  (_: State, suggestionId: string, fieldInstanceId: string | null) => ({
    suggestionId,
    fieldInstanceId,
  }),
  (
    suggestions,
    usage,
    { suggestionId, fieldInstanceId },
  ): (ActionSuggestion & { usedCount: number }) | undefined => {
    const suggestion = suggestions[suggestionId];
    if (!suggestion) {
      return undefined;
    }
    return {
      id: suggestion.id,
      description: suggestion.description,
      why: suggestion.why,
      categoryId: suggestion.categoryId,
      timescaleId: suggestion.timescaleId,
      severityId: suggestion.severityId,
      dueDeterminedBy: suggestion.dueDeterminedBy,
      dueTimescale: suggestion.dueTimescale,
      dueTypeId: suggestion.dueTypeId,
      answerIds: suggestion.answerIds,
      negativeChecklistItemIds: suggestion.negativeChecklistItemIds,
      positiveChecklistItemIds: suggestion.positiveChecklistItemIds,
      usedCount: usage[getAseResultKey(suggestionId, fieldInstanceId)] ?? 0,
      cost: suggestion.cost,
    };
  },
);

const selectAseSuggestedControlsUsage = createSelector(
  selectAseControlsAse,
  (controls) =>
    Object.values(controls).reduce<Record<string, number>>((acc, finding) => {
      if (finding.suggestionId) {
        const key = getAseResultKey(
          finding.suggestionId,
          finding.fieldInstanceId,
        );
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {}),
);

const selectAseTplSuggestedControls = createSelector(
  selectAseTemplate,
  (template: Assessment['template']) => template!.controlSuggestions,
);

export const selectAseTplSuggestedControl = createSelectorCachedResultDeepEqual(
  selectAseTplSuggestedControls,
  selectAseSuggestedControlsUsage,
  (_: State, suggestionId: string, fieldInstanceId: string | null) => ({
    suggestionId,
    fieldInstanceId,
  }),
  (
    suggestions,
    usage,
    { suggestionId, fieldInstanceId },
  ): (ControlSuggestion & { usedCount: number }) | undefined => {
    const suggestion = suggestions[suggestionId];
    if (!suggestion) {
      return undefined;
    }
    return {
      id: suggestion.id,
      description: suggestion.description,
      why: suggestion.why,
      categoryId: suggestion.categoryId,
      questionIds: suggestion.questionIds,
      negativeChecklistItemIds: suggestion.negativeChecklistItemIds,
      positiveChecklistItemIds: suggestion.positiveChecklistItemIds,
      usedCount: usage[getAseResultKey(suggestionId, fieldInstanceId)] ?? 0,
    };
  },
);

const selectAseSectionQuestionToActionIds = createSelector(
  selectAseActionsAse,
  (actions) =>
    Object.values(actions).reduce<{
      sections: Record<string, string[]>;
      questions: Record<string, string[]>;
    }>(
      (acc, action) => {
        let key1: keyof typeof acc | undefined;
        let key2: string | undefined;
        if (action.questionId) {
          key1 = 'questions';
          key2 = getAseResultKey(action.questionId, action.fieldInstanceId);
        } else if (action.sectionId) {
          key1 = 'sections';
          key2 = getAseResultKey(action.sectionId, action.fieldInstanceId);
        }
        if (key1 && key2) {
          if (!acc[key1][key2]) {
            acc[key1][key2] = [];
          }
          acc[key1][key2]!.push(action.id);
        }
        return acc;
      },
      { sections: {}, questions: {} },
    ),
);

const selectAseSectionQuestionToControlIds = createSelector(
  selectAseControlsAse,
  (controls) =>
    Object.values(controls).reduce<{
      sections: Record<string, string[]>;
      questions: Record<string, string[]>;
    }>(
      (acc, control) => {
        let key1: keyof typeof acc | undefined;
        let key2: string | undefined;
        if (control.questionId) {
          key1 = 'questions';
          key2 = getAseResultKey(control.questionId, control.fieldInstanceId);
        } else if (control.sectionId) {
          key1 = 'sections';
          key2 = getAseResultKey(control.sectionId, control.fieldInstanceId);
        }
        if (key1 && key2) {
          if (!acc[key1][key2]) {
            acc[key1][key2] = [];
          }
          acc[key1][key2]!.push(control.id);
        }
        return acc;
      },
      { sections: {}, questions: {} },
    ),
);

export const selectAseQuestionActionIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsNewReviewed,
    selectAseSectionQuestionToActionIds,
    (_: State, questionId: string, fieldInstanceId: string | null) =>
      getAseResultKey(questionId, fieldInstanceId),
  ],
  (show, { questions }, resultKey) => (show ? questions[resultKey] || [] : []),
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseQuestionControlIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsNewReviewed,
    selectAseSectionQuestionToControlIds,
    (_: State, questionId: string, fieldInstanceId: string | null) =>
      getAseResultKey(questionId, fieldInstanceId),
  ],
  (show, { questions }, resultKey) => (show ? questions[resultKey] || [] : []),
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseSectionActionIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsNewReviewed,
    selectAseSectionQuestionToActionIds,
    (_: State, questionId: string, fieldInstanceId: string | null) =>
      getAseResultKey(questionId, fieldInstanceId),
  ],
  (show, { sections }, resultKey) => (show ? sections[resultKey] || [] : []),
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseSectionControlIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsNewReviewed,
    selectAseSectionQuestionToControlIds,
    (_: State, sectionId: string, fieldInstanceId: string | null) =>
      getAseResultKey(sectionId, fieldInstanceId),
  ],
  (show, { sections }, resultKey) => (show ? sections[resultKey] || [] : []),
)((_, sectionId, fieldInstanceId) =>
  getAseResultKey(sectionId, fieldInstanceId),
);

export const selectAseChecklistItemForQuestion = createCachedSelector(
  selectAseTplChecklistItemsForQuestion,
  selectAseResultChecklistItems,
  (_: State, __: string, fieldInstanceId?: string) => fieldInstanceId,
  (checklistItems, checklistItemResults, fieldInstanceId) =>
    checklistItems.reduce<Record<string, ChecklistItemResult>>(
      (acc, checklistItem) => {
        const checklistItemResult =
          checklistItemResults[
            getAseResultKey(checklistItem.id, fieldInstanceId)
          ];
        if (checklistItemResult) {
          acc[checklistItem.id] = checklistItemResult;
        }
        return acc;
      },
      {},
    ),
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseResultChecklistItemsForQuestion = createSelectorCached(
  selectAseTplChecklistItemsForQuestion,
  selectAseResultChecklistItems,
  (_: State, questionId: string, fieldInstanceId: string | null) =>
    fieldInstanceId,
  (checklistItems, checklistItemResults, fieldInstanceId) =>
    checklistItems.reduce<Record<string, ChecklistItemResultEnum>>(
      (acc, checklistItem) => {
        const checklistItemResult =
          checklistItemResults[
            getAseResultKey(checklistItem.id, fieldInstanceId)
          ];
        if (checklistItemResult) {
          acc[checklistItem.id] = checklistItemResult.result;
        }
        return acc;
      },
      {},
    ),
);

export const selectAseQuestionComment = createCachedSelector(
  selectAseResultQuestionComments,
  (_: State, questionId: string, fieldInstanceId?: string) =>
    getAseResultKey(questionId, fieldInstanceId),
  (questionComments, resultKey): string | undefined =>
    questionComments[resultKey],
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseVisibleQuestionResults = createSelector(
  selectAseIsEditable,
  selectAseTplQuestions,
  selectAseTplAnswers,
  selectAseTplRegularQuestionIds,
  selectAseResultQuestions,
  selectAseResultChecklistItems,
  selectAseCriteriaResults,
  selectAseFieldInstancesAseInstanceIdToGroupId,
  selectAseTemplateFields,
  (
    assessmentIsEditable,
    templateQuestions,
    templateAnswers,
    regularQuestionIds,
    resultQuestions,
    checklistItemResults,
    criteriaResults,
    instanceIdToGroupId,
    tplFields,
  ): Record<string, VisibleQuestionResults> => {
    const visibility: Record<string, VisibleQuestionResults> = {};

    const getQuestionResult = (
      questionId: string | number,
      fieldInstanceId: string | null,
    ): string | undefined => {
      const question = templateQuestions[questionId];
      return question?.type === AssessmentQuestionType.question
        ? resultQuestions[getAseResultKey(question.id, fieldInstanceId)]
            ?.answerId
        : undefined;
    };

    const getQuestionVisibleAndResult = (
      questionId: string | number,
      fieldInstanceId: string | null,
    ): undefined | VisibleQuestionResults => {
      const question = templateQuestions[questionId];

      if (!question) {
        return undefined;
      }

      const response: VisibleQuestionResults = {
        hasResult: false,
        checklistItemIds: [],
        checklistItemResults: [],
        answerId: undefined,
      };

      if (question.type === AssessmentQuestionType.checklist) {
        response.checklistItemIds = question.checklistItemIds || [];
        response.checklistItemResults = response.checklistItemIds.reduce<
          ChecklistItemResult[]
        >((acc, checklistItemId) => {
          const checklistItemResult =
            checklistItemResults[
              getAseResultKey(checklistItemId, fieldInstanceId)
            ];
          if (checklistItemResult) {
            acc.push(checklistItemResult);
          }
          return acc;
        }, []);
        response.hasResult = !!Object.keys(response.checklistItemResults)
          .length;
      } else {
        response.answerId = getQuestionResult(question.id, fieldInstanceId);
        response.hasResult = response.answerId !== undefined;
      }

      if (!assessmentIsEditable) {
        return response.hasResult ? response : undefined;
      }

      const isHiddenByParentAnswer =
        question.parent_answer_id &&
        getQuestionResult(
          templateAnswers[question.parent_answer_id]?.parent_question_id ||
            '-1',
          fieldInstanceId,
        ) !== question.parent_answer_id;
      if (isHiddenByParentAnswer) {
        return undefined;
      }

      // Hidden by criteria. We still show question if there is a value but the criteria is false
      // (i.e. user sets result but then changes the field value making the criteria false)
      if (
        !response.hasResult &&
        question.criteria_id &&
        !criteriaResults[question.criteria_id]
      ) {
        return undefined;
      }
      return response;
    };

    // Visible questions
    regularQuestionIds.forEach((questionId) => {
      const isVisibleAndResult = getQuestionVisibleAndResult(questionId, null);
      if (isVisibleAndResult) {
        visibility[questionId] = isVisibleAndResult;
      }
    });

    // Visible questions for assets
    Object.entries(instanceIdToGroupId).forEach(([instanceId, groupId]) => {
      (tplFields.groups[groupId]?.questionIds || []).forEach((questionId) => {
        const isVisibleAndResult = getQuestionVisibleAndResult(
          questionId,
          instanceId,
        );
        if (isVisibleAndResult) {
          visibility[getAseResultKey(questionId, instanceId)] =
            isVisibleAndResult;
        }
      });
    });

    return visibility;
  },
);

export const selectAseTplQuestionIsVisible = createCachedSelector(
  selectAseVisibleQuestionResults,
  (_: State, questionId: string, fieldInstanceId: string | null) =>
    getAseResultKey(questionId, fieldInstanceId),
  (visibleQuestionResults, resultKey): boolean =>
    !!visibleQuestionResults[resultKey],
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

const selectAseTplSectionsAreVisible = createSelector(
  selectAseTplSections,
  selectAseSections,
  selectAseSectionQuestionToActionIds,
  selectAseSectionQuestionToControlIds,
  selectAseVisibleQuestionResults,
  selectAseFieldInstancesAseInstanceIdToGroupId,
  selectAseTemplateFields,
  (
    templateSections,
    resultSections,
    { sections: sectionToActionIds },
    { sections: sectionToControlIds },
    visibleQuestionResults,
    instanceIdToGroupId,
    { groups: fieldGroupsConfig },
  ): Record<string, boolean> => {
    const visibility: Record<string, boolean> = {};
    // General section visibility
    Object.values(templateSections).forEach((section) => {
      visibility[section.id] = !!(
        resultSections[section.id]?.comment ||
        !!sectionToActionIds[getAseResultKey(section.id)]?.length ||
        !!sectionToControlIds[getAseResultKey(section.id)]?.length ||
        section.question_ids.length === 0 ||
        section.question_ids.some(
          (questionId) => !!visibleQuestionResults[getAseResultKey(questionId)],
        )
      );
    });
    // Section visibility for field instances (effectively the field instance itself)
    Object.entries(instanceIdToGroupId).forEach(([instanceId, groupId]) => {
      const questionIds = fieldGroupsConfig[groupId]?.questionIds || [];
      visibility[getAseResultKey('1', instanceId)] = !!(
        questionIds.length === 0 ||
        questionIds.some(
          (questionId) =>
            !!visibleQuestionResults[getAseResultKey(questionId, instanceId)],
        ) ||
        !!sectionToActionIds[getAseResultKey('1', instanceId)]?.length ||
        !!sectionToControlIds[getAseResultKey('1', instanceId)]?.length
      );
    });
    return visibility;
  },
);

export const selectAseTplSectionIsVisible = createCachedSelector(
  selectAseTplSectionsAreVisible,
  (_: AssessmentsStateTree, id: string) => id,
  (sectionsAreVisible, id): boolean => !!sectionsAreVisible[id],
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseQuestionCanBeChanged = createCachedSelector(
  selectAseResultQuestion,
  selectAseTplAnswers,
  selectAseResultQuestions,
  (_: State, __: string, fieldInstanceId?: string) => fieldInstanceId,
  (resultsQuestion, templateAnswers, resultsQuestions, fieldInstanceId) =>
    !resultsQuestion?.answerId ||
    !templateAnswers[resultsQuestion.answerId]?.question_ids.some(
      (questionId) =>
        !!resultsQuestions[getAseResultKey(questionId, fieldInstanceId)]
          ?.answerId,
    ),
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseSuggestedActionsForChecklistItemResults =
  createSelectorCachedResultDeepEqual(
    selectAseTplSuggestedActions,
    (
      _: State,
      checklistItemResults: Record<number, ChecklistItemResultEnum | null>,
    ) => checklistItemResults,
    (suggestedActions, checklistItemResults): ActionSuggestion[] => {
      const { positiveChecklistItemIds, negativeChecklistItemIds } =
        Object.entries(checklistItemResults).reduce<{
          positiveChecklistItemIds: number[];
          negativeChecklistItemIds: number[];
        }>(
          (acc, [checklistItemId, checklistItemResult]) => {
            if (checklistItemResult === ChecklistItemResultEnum.cross) {
              acc.negativeChecklistItemIds.push(toInteger(checklistItemId));
            } else if (checklistItemResult === ChecklistItemResultEnum.tick) {
              acc.positiveChecklistItemIds.push(toInteger(checklistItemId));
            }
            return acc;
          },
          { positiveChecklistItemIds: [], negativeChecklistItemIds: [] },
        );
      if (
        !positiveChecklistItemIds.length &&
        !negativeChecklistItemIds.length
      ) {
        return EMPTY_ARRAY;
      }
      return sortBy(
        Object.values(suggestedActions).filter(
          (suggestedAction) =>
            (suggestedAction.negativeChecklistItemIds?.length &&
              negativeChecklistItemIds.length &&
              intersection(
                suggestedAction.negativeChecklistItemIds,
                negativeChecklistItemIds,
              ).length) ||
            (suggestedAction.positiveChecklistItemIds?.length &&
              positiveChecklistItemIds.length &&
              intersection(
                suggestedAction.positiveChecklistItemIds,
                positiveChecklistItemIds,
              ).length),
        ),
        ['description', 'why'],
      );
    },
  );

export const selectAseSuggestedActionsForAnswerId =
  createSelectorCachedResultDeepEqual(
    selectAseTplSuggestedActions,
    (_: State, answerId: string | null) => answerId,
    (suggestedActions, answerId): ActionSuggestion[] => {
      return answerId
        ? sortBy(
            Object.values(suggestedActions).filter(({ answerIds }) =>
              answerIds.includes(answerId),
            ),
            ['description', 'why'],
          )
        : [];
    },
  );

export const selectAseSuggestedControlsForChecklistItemResults =
  createSelectorCachedResultDeepEqual(
    selectAseTplSuggestedControls,
    (
      _: State,
      checklistItemResults: Record<number, ChecklistItemResultEnum | null>,
    ) => checklistItemResults,
    (suggestedControls, checklistItemResults): ControlSuggestion[] => {
      const { positiveChecklistItemIds, negativeChecklistItemIds } =
        Object.entries(checklistItemResults).reduce<{
          positiveChecklistItemIds: number[];
          negativeChecklistItemIds: number[];
        }>(
          (acc, [checklistItemId, checklistItemResult]) => {
            if (checklistItemResult === ChecklistItemResultEnum.cross) {
              acc.negativeChecklistItemIds.push(toInteger(checklistItemId));
            } else if (checklistItemResult === ChecklistItemResultEnum.tick) {
              acc.positiveChecklistItemIds.push(toInteger(checklistItemId));
            }
            return acc;
          },
          { positiveChecklistItemIds: [], negativeChecklistItemIds: [] },
        );
      if (
        !positiveChecklistItemIds.length &&
        !negativeChecklistItemIds.length
      ) {
        return EMPTY_ARRAY;
      }
      return sortBy(
        Object.values(suggestedControls).filter(
          (suggestedControl) =>
            (suggestedControl.negativeChecklistItemIds?.length &&
              negativeChecklistItemIds.length &&
              intersection(
                suggestedControl.negativeChecklistItemIds,
                negativeChecklistItemIds,
              ).length) ||
            (suggestedControl.positiveChecklistItemIds?.length &&
              positiveChecklistItemIds.length &&
              intersection(
                suggestedControl.positiveChecklistItemIds,
                positiveChecklistItemIds,
              ).length),
        ),
        ['description', 'why'],
      );
    },
  );

export const selectAseSuggestedControlsForQuestionId =
  createSelectorCachedResultDeepEqual(
    selectAseTplSuggestedControls,
    (_: State, questionId: string) => questionId,
    (suggestedControls, questionId): ControlSuggestion[] => {
      return sortBy(
        Object.values(suggestedControls).filter(({ questionIds }) =>
          questionIds.includes(questionId),
        ),
        ['description', 'why'],
      );
    },
  );

const selectAseActionIdsByPreviousParentForSectionsAndQuestions =
  createSelector(
    [
      selectAseIsEditable,
      selectAseActionsAst,
      selectAseTplSectionsAreVisible,
      selectAseVisibleQuestionResults,
    ],
    (editable, assetActions, sectionsAreVisible, visibleQuestionResults) =>
      (editable ? assetActions : []).reduce<
        Record<'section' | 'question', Record<string, string[]>>
      >(
        (acc, assetAction) => {
          const parent = assetAction.previousParents
            .slice()
            .reverse()
            .find((p) =>
              p.type === 'section'
                ? sectionsAreVisible[
                    getAseResultKey(p.id, assetAction.fieldInstanceId)
                  ]
                : visibleQuestionResults[
                    getAseResultKey(p.id, assetAction.fieldInstanceId)
                  ],
            ) || { id: '1', type: 'section' };
          const key = getAseResultKey(parent.id, assetAction.fieldInstanceId);
          if (!acc[parent.type][key]) {
            acc[parent.type][key] = [];
          }
          acc[parent.type][key]!.push(assetAction.id);
          return acc;
        },
        {
          question: {},
          section: {},
        },
      ),
    {
      memoizeOptions: {
        resultEqualityCheck: isEqual,
      },
    },
  );

const selectAseUnreviewedActions = createSelector(
  selectAseActionsAst,
  selectAseActionsAse,
  (assetActions, aseActions) =>
    assetActions.filter(({ id }) => !aseActions[id]),
);

const selectAseUnreviewedControls = createSelector(
  selectAseControlsAst,
  selectAseControlsAse,
  (assetControls, aseControls) =>
    assetControls.filter(({ id }) => !aseControls[id]),
);

export const selectAseActionPreviousParent = createCachedSelector(
  selectAseActionAstAse,
  selectAseTplSectionsAreVisible,
  selectAseVisibleQuestionResults,
  (action, sectionsAreVisible, visibleQuestionResults): FindingPreviousParent =>
    action?.previousParents
      .slice()
      .reverse()
      .find((parent) =>
        parent.type === 'section'
          ? sectionsAreVisible[
              getAseResultKey(parent.id, action.fieldInstanceId)
            ]
          : visibleQuestionResults[
              getAseResultKey(parent.id, action.fieldInstanceId)
            ],
      ) || { id: '1', type: 'section' },
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseQuestionUnreviewedActionIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsUnreviewed,
    selectAseActionIdsByPreviousParentForSectionsAndQuestions,
    (_: State, s: string, f: string) => getAseResultKey(s, f),
    selectAseActionIdsAse,
  ],
  (
    showUnreviewed,
    { question: questionActions },
    resultKey,
    aseActionIds,
  ): string[] =>
    showUnreviewed
      ? (questionActions[resultKey] || []).filter(
          (actionId) => !aseActionIds.includes(actionId),
        )
      : [],
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseSectionUnreviewedActionIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsUnreviewed,
    selectAseActionIdsByPreviousParentForSectionsAndQuestions,
    (_: State, s: string, f: string) => getAseResultKey(s, f),
    selectAseActionIdsAse,
  ],
  (
    showUnreviewed,
    { section: sectionActions },
    resultKey,
    aseActionIds,
  ): string[] =>
    showUnreviewed
      ? (sectionActions[resultKey] || []).filter(
          (actionId) => !aseActionIds.includes(actionId),
        )
      : [],
)((_, sectionId, fieldInstanceId) =>
  getAseResultKey(sectionId, fieldInstanceId),
);

const selectAseControlIdsByPreviousParentForSectionsAndQuestions =
  createSelector(
    selectAseControlsAst,
    selectAseIsEditable,
    selectAseTplSectionsAreVisible,
    selectAseVisibleQuestionResults,
    (assetControls, editable, sectionsAreVisible, visibleQuestionResults) =>
      (editable ? assetControls : []).reduce<
        Record<'section' | 'question', Record<string, string[]>>
      >(
        (acc, assetControl) => {
          const parent = assetControl.previousParents
            .slice()
            .reverse()
            .find((p) =>
              p.type === 'section'
                ? sectionsAreVisible[
                    getAseResultKey(p.id, assetControl.fieldInstanceId)
                  ]
                : visibleQuestionResults[
                    getAseResultKey(p.id, assetControl.fieldInstanceId)
                  ],
            ) || { id: '1', type: 'section' };
          const key = getAseResultKey(parent.id, assetControl.fieldInstanceId);
          if (!acc[parent.type][key]) {
            acc[parent.type][key] = [];
          }
          acc[parent.type][key]!.push(assetControl.id);
          return acc;
        },
        {
          question: {},
          section: {},
        },
      ),
    {
      memoizeOptions: {
        resultEqualityCheck: isEqual,
      },
    },
  );

export const selectAseControlPreviousParent = createCachedSelector(
  selectAseControlAstAse,
  selectAseTplSectionsAreVisible,
  selectAseVisibleQuestionResults,
  (
    control,
    sectionsAreVisible,
    visibleQuestionResults,
  ): FindingPreviousParent =>
    control?.previousParents
      .slice()
      .reverse()
      .find((p) =>
        p.type === 'section'
          ? sectionsAreVisible[getAseResultKey(p.id, control.fieldInstanceId)]
          : visibleQuestionResults[
              getAseResultKey(p.id, control.fieldInstanceId)
            ],
      ) || { id: '1', type: 'section' },
)((_: AssessmentsStateTree, id: string) => id);

export const selectAseQuestionUnreviewedControlIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsUnreviewed,
    selectAseControlIdsByPreviousParentForSectionsAndQuestions,
    (_: State, s: string, f: string) => getAseResultKey(s, f),
    selectAseControlIdsAse,
  ],
  (
    showUnreviewed,
    { question: questionControls },
    resultKey,
    aseControlIds,
  ): string[] =>
    showUnreviewed
      ? (questionControls[resultKey] || []).filter(
          (controlId) => !aseControlIds.includes(controlId),
        )
      : [],
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseSectionUnreviewedControlIds = createCachedSelector(
  [
    selectAseExplorerOptionFindingsUnreviewed,
    selectAseControlIdsByPreviousParentForSectionsAndQuestions,
    (_: AssessmentsStateTree, s: string, f: string) => getAseResultKey(s, f),
    selectAseControlIdsAse,
  ],
  (
    showUnreviewed,
    { section: sectionControls },
    resultKey,
    aseControlIds,
  ): string[] =>
    showUnreviewed
      ? (sectionControls[resultKey] || []).filter(
          (controlId) => !aseControlIds.includes(controlId),
        )
      : [],
)((_, sectionId, fieldInstanceId) =>
  getAseResultKey(sectionId, fieldInstanceId),
);

export const selectAseQuestionLinkedItems = createCachedSelector(
  selectAseTplQuestions,
  selectAseTplAnswers,
  selectAseResultQuestions,
  selectAseResultChecklistItems,
  selectAseActionsAse,
  selectAseControlsAse,
  (_: State, questionId: string, fieldInstanceId: string | null) => ({
    questionId,
    fieldInstanceId,
  }),
  (
    questions,
    answers,
    questionResults,
    checklistItemResults,
    actions,
    controls,
    { questionId, fieldInstanceId },
  ): {
    type:
      | AssessmentItemTypes.ACTION
      | AssessmentItemTypes.CONTROL
      | AssessmentItemTypes.QUESTION
      | AssessmentItemTypes.CHECKLISTITEM
      | AssessmentItemTypes.QUESTIONCOMMENTS;
    id: string;
  }[] => {
    const itemsToBeDeleted: {
      type:
        | AssessmentItemTypes.ACTION
        | AssessmentItemTypes.CONTROL
        | AssessmentItemTypes.QUESTION
        | AssessmentItemTypes.CHECKLISTITEM
        | AssessmentItemTypes.QUESTIONCOMMENTS;
      id: string;
    }[] = [];
    const questionHasActions = Object.values(actions).reduce<
      Record<string, string[]>
    >((acc, action) => {
      if (action.questionId) {
        const key = getAseResultKey(action.questionId, action.fieldInstanceId);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key]!.push(action.id);
      }
      return acc;
    }, {});
    const questionHasControls = Object.values(controls).reduce<
      Record<string, string[]>
    >((acc, control) => {
      if (control.questionId) {
        const key = getAseResultKey(
          control.questionId,
          control.fieldInstanceId,
        );
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key]!.push(control.id);
      }
      return acc;
    }, {});
    const cascade = (thsQuestionId: string) => {
      const question = questions[thsQuestionId];
      const resultKey = getAseResultKey(thsQuestionId, fieldInstanceId);
      let questionHasResult = false;
      if (question?.type === AssessmentQuestionType.checklist) {
        question.checklistItemIds?.forEach((checklistItemId) => {
          const checklistResultKey = getAseResultKey(
            checklistItemId,
            fieldInstanceId,
          );
          if (checklistItemResults[checklistResultKey]) {
            questionHasResult = true;
            itemsToBeDeleted.push({
              id: checklistResultKey,
              type: AssessmentItemTypes.CHECKLISTITEM,
            });
          }
        });
      } else if (questionResults[resultKey]?.answerId) {
        questionHasResult = true;
        itemsToBeDeleted.push({
          id: resultKey,
          type: AssessmentItemTypes.QUESTION,
        });
        (question?.answer_ids || []).forEach((answerId) => {
          const answer = answers[answerId];
          (answer?.question_ids || []).forEach((thsSubQuestionId) => {
            cascade(thsSubQuestionId);
          });
        });
      }
      if (questionHasResult) {
        itemsToBeDeleted.push({
          id: resultKey,
          type: AssessmentItemTypes.QUESTIONCOMMENTS,
        });
        questionHasActions[resultKey]?.forEach((actionId) => {
          itemsToBeDeleted.push({
            id: actionId,
            type: AssessmentItemTypes.ACTION,
          });
        });
        questionHasControls[resultKey]?.forEach((controlId) => {
          itemsToBeDeleted.push({
            id: controlId,
            type: AssessmentItemTypes.CONTROL,
          });
        });
      }
    };
    cascade(questionId);
    return itemsToBeDeleted;
  },
)((_, questionId, fieldInstanceId) =>
  getAseResultKey(questionId, fieldInstanceId),
);

export const selectAseFindingLocations = createSelector(
  [
    selectAseActionsAse,
    selectAseControlsAse,
    selectAseUnreviewedActions,
    selectAseUnreviewedControls,
  ],
  (
    actions,
    controls,
    unreviewedActions,
    unreviewedControls,
  ): { locations: string[]; unreviewedLocations: string[] } => ({
    locations: naturalOrderBy(
      arrayUnique([
        ...Object.values(actions).map((item) => item.where),
        ...Object.values(controls).map((item) => item.where),
      ]).filter(filterArrayEmpty),
    ),
    unreviewedLocations: naturalOrderBy(
      arrayUnique(
        [
          ...unreviewedActions.map((item) => item.where),
          ...unreviewedControls.map((item) => item.where),
        ].filter(filterArrayEmpty),
      ),
    ),
  }),
);

// Template Fields

export const selectAseTplFieldGroup = createCachedSelector(
  selectAseDataFieldGroups,
  selectAseDataFieldFields,
  (_: State, groupId: string) => groupId,
  (groups, fields, groupId): AseTplFieldGroupWithCalculatedInfo | undefined => {
    const group = groups[groupId];
    return !group
      ? undefined
      : { ...group, calculatedInfo: getFieldGroupInfo(group, fields) };
  },
)((_: State, groupId: string) => groupId);

export const selectAseTplFieldToGroupId = createCachedSelector(
  selectAseDataFieldFieldsToGroups,
  (_: State, fieldId: string) => fieldId,
  (fieldToGroup, fieldId): string | undefined => fieldToGroup[fieldId],
)((_, fieldId) => fieldId);

export const selectAseTplFieldToGroup = createCachedSelector(
  selectAseTplFieldToGroupId,
  selectAseDataFieldGroups,
  selectAseDataFieldFields,
  (groupId, groups, fields): AseTplFieldGroupWithCalculatedInfo | undefined => {
    const group = groupId && groups[groupId];
    return !group
      ? undefined
      : { ...group, calculatedInfo: getFieldGroupInfo(group, fields) };
  },
)((_, fieldId) => fieldId);

export const selectAseTplFieldSectionsForExplorer = createSelector(
  selectAseTemplateFields,
  selectAseOtherDataFields,
  (
    aseFieldConfig,
    orgFieldConfig,
  ): {
    id: string;
    name: string;
    groups: {
      id: string;
      name: string;
      repeatable: boolean;
      fields: { name: string; id: string }[];
    }[];
  }[] => {
    const sections: {
      id: string;
      name: string;
      groups: {
        id: string;
        name: string;
        repeatable: boolean;
        fields: { id: string; name: string }[];
      }[];
    }[] = [];

    if (!Object.keys(aseFieldConfig.groups).length) {
      return sections;
    }

    orgFieldConfig.sectionIds.forEach((sectionId) => {
      const section = orgFieldConfig.sections[sectionId]!;
      const sectionGroups: (typeof sections)[0]['groups'] = [];
      section.groupIds.forEach((groupId) => {
        const group = orgFieldConfig.groups[groupId]!;
        const groupFields: (typeof sections)[0]['groups'][0]['fields'] = [];
        if (aseFieldConfig.groups[groupId]) {
          group.fieldIds.forEach((fieldId) => {
            const field = orgFieldConfig.fields[fieldId]!;
            groupFields.push({
              name: field.label,
              id: field.id,
            });
          });
        }
        if (groupFields.length) {
          sectionGroups.push({
            name: group.name,
            id: group.id,
            repeatable: group.repeatable,
            fields: groupFields,
          });
        }
      });
      if (sectionGroups.length) {
        sections.push({
          name: section.name,
          id: section.id,
          groups: sectionGroups,
        });
      }
    });
    return sections;
  },
);

/**
 * Select instances included in the assessment. Don't include instances
 * that are not included in the assessment. Results from assessment
 * and asset are merged together
 */
const selectAseFieldInstancesAseMerged = createSelector(
  selectAseFieldInstancesAst,
  selectAseFieldInstancesAse,
  (assetInstances, aseInstances) =>
    Object.values(aseInstances).reduce<
      Record<string, AseFieldInstanceWithFormattedAutoId>
    >((acc, aseInstance) => {
      const assetInstance = assetInstances[aseInstance.instanceId];
      if (assetInstance) {
        acc[aseInstance.instanceId] = {
          instanceId: assetInstance.instanceId,
          action: assetInstance.action,
          autoId: assetInstance.autoId,
          createdTs: assetInstance.createdTs,
          groupId: assetInstance.groupId,
          updatedTs: assetInstance.updatedTs,
          reference: assetInstance.reference,
          draft: assetInstance.draft,
          draftAutoId: undefined,
          formattedAutoId: assetInstance.formattedAutoId,
          inAssessment: true,
          results: {
            ...assetInstance.results,
            ...aseInstance.results,
          },
          checks: aseInstance.checks,
        };
      } else {
        acc[aseInstance.instanceId] = aseInstance;
      }
      return acc;
    }, {}),
);

export const selectAseFieldInstancesAstAseMerged = createSelector(
  selectAseFieldInstancesAst,
  selectAseFieldInstancesAseMerged,
  (assetInstances, aseInstances) =>
    arrayUnique([
      ...Object.keys(aseInstances),
      ...Object.keys(assetInstances),
    ]).reduce<Record<string, AseFieldInstanceWithFormattedAutoId>>(
      (acc, instanceId) => {
        acc[instanceId] =
          aseInstances[instanceId] || assetInstances[instanceId]!;
        return acc;
      },
      {},
    ),
);

export const selectAseFieldInstanceAstAseMerged = createCachedSelector(
  selectAseFieldInstancesAstAseMerged,
  (_: State, instanceId: string) => instanceId,
  (instances, instanceId) => instances[instanceId],
)((_: State, instanceId: string) => instanceId);

export const selectAseFieldInstanceAse = createCachedSelector(
  (_: State, instanceId: string) => instanceId,
  selectAseFieldInstancesAse,
  (instanceId, instances) => instances[instanceId],
)((_, instanceId) => instanceId);

export const selectAseFieldInstanceAstAse = createCachedSelector(
  selectAseFieldInstanceAse,
  selectAseFieldInstanceAst,
  (aseInstance, assetInstance) => aseInstance || assetInstance,
)((_, instanceId) => instanceId);

const selectAseFieldValueAssessment = createCachedSelector(
  selectAseFieldResultsAse,
  (_: State, fieldId: string) => fieldId,
  (results, fieldId) => results[fieldId]?.value,
)((_: State, fieldId: string) => fieldId);

const selectAseFieldValueAsset = createCachedSelector(
  selectAseFieldResultsAst,
  (_: State, fieldId: string) => fieldId,
  (results, fieldId) => results[fieldId]?.value,
)((_: State, fieldId: string) => fieldId);

export const selectAseFieldInstanceChildInstanceIdsAst =
  createSelectorCachedResultDeepEqual(
    [
      (_: State, input: InstanceParentParams) => input,
      (_: State, input: InstanceParentParams) =>
        selectAseFieldInstanceAst(_, input?.instanceId || '-1'),
      (_: State, input: InstanceParentParams) =>
        selectAseFieldValueAsset(
          _,
          input?.fieldId && !input.instanceId ? input.fieldId : '-1',
        ),
    ],
    (input, instance, fieldResult): string[] | null =>
      input?.instanceId && input.fieldId
        ? ((instance?.results[input.fieldId]?.value || []) as string[])
        : input?.fieldId
          ? ((fieldResult || []) as string[])
          : null,
  );

export const selectAseFieldInstanceChildInstanceIdsAstAse =
  createSelectorCachedResultDeepEqual(
    [
      (_: State, input: InstanceParentParams) => input,
      (_: State, input: InstanceParentParams) =>
        selectAseFieldInstanceAse(_, input?.instanceId || '-1'),
      (_: State, input: InstanceParentParams) =>
        selectAseFieldValueAssessment(
          _,
          input?.fieldId && !input.instanceId ? input.fieldId : '-1',
        ),
      selectAseFieldInstanceChildInstanceIdsAst,
    ],
    (input, instance, fieldResult, assetResults): string[] | null =>
      input?.instanceId && input.fieldId
        ? ((instance?.results[input.fieldId]?.value ||
            assetResults) as string[])
        : input?.fieldId
          ? ((fieldResult || assetResults) as string[])
          : null,
  );

// Merged field instance
export const selectAseFieldInstanceAseMerged = createCachedSelector(
  [
    selectAseFieldInstancesAseMerged,
    (_: State, instanceId: string) => instanceId,
  ],
  (instances, instanceId) => instances[instanceId],
)((_: State, instanceId: string) => instanceId);

const selectAseTempDataRepeatables = createSelector(
  selectAse,
  (assessment) => assessment.tempData.repeatables,
);
export const selectAseTempDataInstanceAseMerged = createSelectorCached(
  [selectAseTempDataRepeatables, (_: State, instanceId: string) => instanceId],
  (instances, instanceId) => instances[instanceId],
);

const selectAseFieldInstanceQrCodeFields = createSelector(
  [selectAseTemplateFields, selectAseDataFieldGroups, selectAseDataFieldFields],
  (tplFields, groups, fields) =>
    Object.values(groups).reduce<AseTplFieldField[]>(
      (acc, group) =>
        group.repeatable && tplFields.groups[group.id]
          ? group.fieldIds.reduce((acc1, fieldId) => {
              const field = fields[fieldId];
              if (
                field &&
                field.type === FieldTypes.QR &&
                tplFields.fields[fieldId]
              ) {
                acc1.push(field);
              }
              return acc1;
            }, acc)
          : acc,
      [],
    ),
);

export const selectAseHasFieldInstancesWithQrData = createSelector(
  [selectAseFieldInstanceQrCodeFields, selectAseFieldInstancesAstAseMerged],
  (qrFields, instances): boolean => {
    if (qrFields.length) {
      for (const instance of Object.values(instances)) {
        for (const qrField of qrFields) {
          const value = instance.results[qrField.id]?.value;
          if (value && typeof value === 'string') {
            return true;
          }
        }
      }
    }
    return false;
  },
);

export const selectAseFieldInstanceWithQrCode = createSelector(
  [
    selectAseFieldInstanceQrCodeFields,
    selectAseFieldInstancesAstAseMerged,
    (_: State, qrData: string) => qrData,
  ],
  (
    qrFields,
    instances,
    qrData,
  ): FieldInstanceWithFormattedAutoId | undefined => {
    if (qrFields.length) {
      for (const instance of Object.values(instances)) {
        for (const qrField of qrFields) {
          const value = instance.results[qrField.id]?.value;
          if (value && value === qrData) {
            return instance;
          }
        }
      }
    }
    return undefined;
  },
);

// Merged field instances for group
export const selectAseFieldInstancesForGroup = createCachedSelector(
  [
    (_: State, { groupId }: SelectInstancesForGroup) => groupId,
    (_: State, { inAssessmentOnly }: SelectInstancesForGroup) =>
      inAssessmentOnly,
    (_: State, { parent }: SelectInstancesForGroup) =>
      selectAseFieldInstanceChildInstanceIdsAstAse(_, parent),
    selectAseFieldInstancesAstAseMerged,
  ],
  (
    groupId,
    inAssessmentOnly,
    parentInstanceIds,
    instances,
  ): Record<string, FieldInstanceWithFormattedAutoId> =>
    parentInstanceIds !== null
      ? parentInstanceIds.reduce<
          Record<string, FieldInstanceWithFormattedAutoId>
        >((acc, instanceId) => {
          const instance = instances[instanceId];
          if (instance && (!inAssessmentOnly || instance.inAssessment)) {
            acc[instanceId] = instance;
          }
          return acc;
        }, {})
      : Object.values(instances).reduce<
          Record<string, FieldInstanceWithFormattedAutoId>
        >((acc, instance) => {
          if (
            instance.groupId === groupId &&
            (!inAssessmentOnly || instance.inAssessment)
          ) {
            acc[instance.instanceId] = instance;
          }
          return acc;
        }, {}),
)(forGroupCacheKeyFnc);

const selectAseFieldInstanceIdsAstForGroup = createCachedSelector(
  selectAseFieldInstancesAst,
  (_: State, { groupId }: SelectInstancesForGroup) => groupId,
  (_: State, { parent }: SelectInstancesForGroup) =>
    selectAseFieldInstanceChildInstanceIdsAst(_, parent),
  (instances, groupId, parentInstanceIds): string[] =>
    parentInstanceIds !== null
      ? parentInstanceIds
      : Object.entries(instances).reduce<string[]>(
          (acc, [instanceId, { groupId: instanceGroupId }]) => {
            if (instanceGroupId === groupId) {
              acc.push(instanceId);
            }
            return acc;
          },
          [],
        ),
)(forGroupCacheKeyFnc);

export const selectAseFieldInstanceBelongsToInstances = createSelectorCached(
  [
    selectAseDataFieldFieldIdsWhereTypeIsRepeatable,
    selectAseFieldInstancesAstAseMerged,
    (_: State, instanceId: string) => instanceId,
  ],
  (fieldIds, instances, instanceId) =>
    Object.values(instances).reduce<string[]>(
      (acc, instance) =>
        fieldIds.reduce((acc1, fieldId) => {
          const childInstanceIds = (instance.results[fieldId]?.value ||
            []) as string[];
          if (
            childInstanceIds.length &&
            childInstanceIds.includes(instanceId)
          ) {
            acc1.push(instance.instanceId);
          }
          return acc1;
        }, acc),
      [],
    ),
);

export const selectAseFieldInstanceIdsForGroup = createCachedSelector(
  selectAseFieldInstancesForGroup,
  (instances): string[] => Object.keys(instances),
)(forGroupCacheKeyFnc);

export const selectAseFieldInstancesCountForGroup = createCachedSelector(
  selectAseFieldInstanceIdsForGroup,
  (instanceIds): number => instanceIds.length,
)(forGroupCacheKeyFnc);

export const selectAseFieldInstanceIdsToAddForGroupFilteredAndSorted =
  createCachedSelector(
    [
      (state: State, { groupId }: SelectInstancesForGroup) =>
        selectAseTplFieldGroup(state, groupId),
      (state: State, { groupId }: SelectInstancesForGroup) =>
        selectAseFieldInstancesForGroup(state, {
          groupId,
          parent: null,
          inAssessmentOnly: false,
        }),
      (state: State, { parent }: SelectInstancesForGroup) =>
        selectAseFieldInstanceChildInstanceIdsAstAse(state, parent || null),
      (_: State, { filtered }: SelectInstancesForGroup) => filtered,
      (state: State, { groupId }: SelectInstancesForGroup) =>
        selectAseFieldGroupFilter(
          state,
          getRepeatableFilterKey(groupId, 'adding'),
        ),
      selectAseDataFieldFields,
    ],
    (
      group,
      rawInstances,
      addedInstanceIds,
      filtered,
      filters,
      fields,
    ): string[] => {
      let instances =
        addedInstanceIds !== null
          ? Object.fromEntries(
              Object.entries(rawInstances).filter(
                ([instanceId]) => !addedInstanceIds.includes(instanceId),
              ),
            )
          : Object.fromEntries(
              Object.entries(rawInstances).filter(
                ([, { inAssessment }]) => !inAssessment,
              ),
            );
      if (filtered) {
        // Filter checks
        const filterChecks = Object.entries(filters.checks);
        if (filterChecks.length) {
          instances = Object.fromEntries(
            Object.entries(instances).filter(([, instance]) =>
              filterChecks.every(([checkId, checkStatus]) =>
                checkStatus === 'required'
                  ? instance.checks?.[checkId]?.schedule?.statusId !==
                    FieldInstanceCheckScheduleStatusEnum.completed
                  : instance.checks?.[checkId]?.schedule?.statusId ===
                    FieldInstanceCheckScheduleStatusEnum.completed,
              ),
            ),
          );
        }
        const filterAvailabilityCategories =
          filters.availabilityCategories || [];
        if (filterAvailabilityCategories.length) {
          const now = new Date();
          instances = filterByAvailabilityCategory(
            instances,
            filterAvailabilityCategories,
            now,
          );
        }
        // Filter search term
        instances = group
          ? returnFieldInstanceIdsMatchingSearchTerm({
              group,
              fields,
              instances,
              searchTerms: getSearchTermsFromSearchValue(filters.search),
            })
          : {};
      }
      // Order
      const instanceValues = Object.values(instances);
      if (filters.order.startsWith('check-')) {
        const [, checkId, type, direction] = filters.order.split('-');
        const secondaryOrdering =
          FieldRepeatableOrderConfig[FieldRepeatableOrderEnum.createdAsc];
        const directions = [
          direction as 'asc' | 'desc',
          secondaryOrdering.direction,
        ];
        return orderBy(
          instanceValues.reduce<{ id: string; values: string[] }[]>(
            (acc, instance) => {
              if (type === 'attempted') {
                acc.push({
                  id: instance.instanceId,
                  values: [
                    `${instance.checks?.[checkId!]?.lastAttempted?.date || 0}`,
                    instance[secondaryOrdering.key].toString(),
                  ],
                });
              } else {
                acc.push({
                  id: instance.instanceId,
                  values: [
                    `${instance.checks?.[checkId!]?.lastCompleted?.date || 0}`,
                    instance[secondaryOrdering.key].toString(),
                  ],
                });
              }
              return acc;
            },
            [],
          ),
          [(v) => v.values[0], (v) => v.values[1]],
          directions,
        ).map(({ id }) => id);
      }
      const orderValueConfig =
        FieldRepeatableOrderConfig[
          validateEnumFallback(
            FieldRepeatableOrderEnum,
            filters.order,
            FieldRepeatableOrderEnum.createdAsc,
          )
        ];
      return orderBy(
        instanceValues,
        [orderValueConfig.key],
        [orderValueConfig.direction],
      ).map(({ instanceId }) => instanceId);
    },
  )(forGroupCacheKeyFnc);

const selectAseFieldInstancesRequiredFieldValues = createSelector(
  [selectAse],
  (ase): Record<string, number> => ase.stats.fieldInstanceRequiredResultsCount,
);

export const selectAseFieldInstanceRequiredFieldValues = createSelectorCached(
  [selectAse, (_: State, instanceId: string) => instanceId],
  (ase, instanceId): number =>
    ase.stats.fieldInstanceRequiredResultsCount[instanceId] || 0,
);

export const selectAseFieldGroupsRequiredFieldValues = createSelectorCached(
  [selectAse],
  (ase): Record<string, number> => ase.stats.fieldGroupRequiredResultsCount,
);

export const selectAseFieldGroupRequiredFieldValues = createSelectorCached(
  [selectAse, (_: State, groupId: string) => groupId],
  (ase, groupId): number =>
    ase.stats.fieldGroupRequiredResultsCount[groupId] || 0,
);

export const selectAseFieldInstanceIdsForGroupFilteredAndSorted =
  createCachedSelector(
    [
      (state: State, { groupId }: SelectInstancesForGroup) =>
        selectAseTplFieldGroup(state, groupId),
      selectAseFieldInstancesForGroup,
      (state: State, { groupId }: SelectInstancesForGroup) =>
        selectAseFieldGroupFilter(
          state,
          getRepeatableFilterKey(groupId, 'added'),
        ),
      selectAseDataFieldFields,
      selectAseFieldInstancesRequiredFieldValues,
    ],
    (group, rawInstances, filters, fields, requiredFieldValues): string[] => {
      let instances = rawInstances;
      // Filter checks
      const filterChecks = Object.entries(filters.checks);
      const hasFilterChecks = !!filterChecks.length;
      if (filters.requiredFields || hasFilterChecks) {
        instances = Object.fromEntries(
          Object.entries(instances).filter(
            ([, instance]) =>
              (!filters.requiredFields ||
                (requiredFieldValues[instance.instanceId] || 0) > 0) &&
              (!hasFilterChecks ||
                filterChecks.every(([checkId, checkStatus]) =>
                  checkStatus === 'required'
                    ? instance.checks?.[checkId]?.schedule?.statusId !==
                      FieldInstanceCheckScheduleStatusEnum.completed
                    : instance.checks?.[checkId]?.schedule?.statusId ===
                      FieldInstanceCheckScheduleStatusEnum.completed,
                )),
          ),
        );
      }

      const filterAvailabilityCategories = filters.availabilityCategories || [];
      if (filterAvailabilityCategories.length) {
        const now = new Date();
        instances = filterByAvailabilityCategory(
          instances,
          filterAvailabilityCategories,
          now,
        );
      }
      // Filter incomplete fields
      if (filters.requiredFields) {
        instances = Object.fromEntries(
          Object.entries(instances).filter(
            ([, instance]) =>
              (requiredFieldValues[instance.instanceId] || 0) > 0,
          ),
        );
      }

      // Filter search term
      instances = group
        ? returnFieldInstanceIdsMatchingSearchTerm({
            group,
            fields,
            instances,
            searchTerms: getSearchTermsFromSearchValue(filters.search),
          })
        : {};
      // Order
      if (filters.order.startsWith('check-')) {
        const [, checkId, type, direction] = filters.order.split('-');
        const secondaryOrdering =
          FieldRepeatableOrderConfig[FieldRepeatableOrderEnum.createdAsc];
        const directions = [
          direction as 'asc' | 'desc',
          secondaryOrdering.direction,
        ];
        return orderBy(
          Object.values(instances).reduce<{ id: string; values: string[] }[]>(
            (acc, instance) => {
              if (type === 'attempted') {
                acc.push({
                  id: instance.instanceId,
                  values: [
                    `${instance.checks?.[checkId!]?.lastAttempted?.date || 0}`,
                    instance[secondaryOrdering.key].toString(),
                  ],
                });
              } else {
                acc.push({
                  id: instance.instanceId,
                  values: [
                    `${instance.checks?.[checkId!]?.lastCompleted?.date || 0}`,
                    instance[secondaryOrdering.key].toString(),
                  ],
                });
              }
              return acc;
            },
            [],
          ),
          [(v) => v.values[0], (v) => v.values[1]],
          directions,
        ).map(({ id }) => id);
      }
      const orderValueConfig =
        FieldRepeatableOrderConfig[
          validateEnumFallback(
            FieldRepeatableOrderEnum,
            filters.order,
            FieldRepeatableOrderEnum.createdAsc,
          )
        ];
      return orderBy(
        Object.values(instances),
        [orderValueConfig.key],
        [orderValueConfig.direction],
      ).map(({ instanceId }) => instanceId);
    },
  )(forGroupCacheKeyFnc);

export const selectAseFieldInstanceChildInstanceIsInParentAst =
  createCachedSelector(
    [
      selectAseFieldInstanceChildInstanceIdsAst,
      (_: State, __: InstanceParentParams, instanceId: string) => instanceId,
    ],
    (value, instanceId): boolean => (value || []).includes(instanceId),
  )(
    (_: State, parent: InstanceParentParams, instanceId: string) =>
      `${instanceId}-${
        parent ? `${parent.fieldId || 0}-${parent.instanceId || 0}` : '-0-0'
      }`,
  );

export const selectAseFieldInstanceChildInstanceIsInParentAstAse =
  createCachedSelector(
    [
      selectAseFieldInstanceChildInstanceIdsAstAse,
      (_: State, __: InstanceParentParams, instanceId: string) => instanceId,
    ],
    (value, instanceId): boolean => (value || []).includes(instanceId),
  )(
    (_: State, parent: InstanceParentParams, instanceId: string) =>
      `${instanceId}-${
        parent ? `${parent.fieldId || 0}-${parent.instanceId || 0}` : '-0-0'
      }`,
  );

export const selectAseFieldInstanceNextAutoIdForGroup = createCachedSelector(
  (_: State, groupId: string) => groupId,
  selectAseFieldInstanceNextAutoIds,
  (groupId, nextAutoIds): number => nextAutoIds[groupId] || 1,
)((_, groupId) => groupId);

export const selectAseFieldInstanceNextDraftAutoIdForGroup =
  createCachedSelector(
    (_: State, groupId: string) =>
      selectAseFieldInstancesForGroup(_, {
        groupId,
        parent: null,
        inAssessmentOnly: false,
      }),
    (instances): number =>
      Object.values(instances).reduce(
        (acc, instance) =>
          instance.draftAutoId && acc < instance.draftAutoId
            ? instance.draftAutoId
            : acc,
        0,
      ) + 1,
  )((_, groupId) => groupId);

const selectAseTemplateFieldGroupsConfig = createSelector(
  selectAseTemplateFields,
  (aseFieldConfig) => aseFieldConfig.groups,
);

export const selectAseTemplateFieldGroupConfig = createCachedSelector(
  selectAseTemplateFieldGroupsConfig,
  (_: State, groupId: string) => groupId,
  (groupsConfig, groupId) => groupsConfig[groupId]!,
)((_, groupId) => groupId);

const selectAseFilterGroups = createSelector(
  selectAse,
  (assessment) => assessment.filterGroups,
);

export const selectAseFieldGroupFilter = createCachedSelector(
  selectAseFilterGroups,
  (_: State, groupId: string) => groupId,
  (filterGroups, groupId): Assessment['filterGroups'][string] =>
    filterGroups[groupId] || defaultGroupExplorerOptions,
)((_: State, groupId: string) => groupId);

export const selectAseTplFieldInstanceChecksForGroup = createCachedSelector(
  selectAseTemplateFieldGroupConfig,
  selectAseTplFieldInstanceChecks,
  (groupConfig, checks): Record<string, AseTplInstanceCheck> => {
    return groupConfig.checkIds.reduce<Record<string, AseTplInstanceCheck>>(
      (acc, checkId) => {
        const check = checks[checkId];
        if (check) {
          acc[checkId] = check;
        }
        return acc;
      },
      {},
    );
  },
)((_: AssessmentsStateTree, groupId: string) => groupId);

const selectAseTplFieldInstanceDefaultChecksByGroup = createSelector(
  selectAse,
  (assessment) => assessment.assetData.defaultInstanceChecks,
);

export const selectAseTplFieldInstanceDefaultChecksForGroup =
  createCachedSelector(
    selectAseTplFieldInstanceDefaultChecksByGroup,
    (_: State, groupId: string) => groupId,
    (
      defaultChecksByGroup,
      groupId,
    ): Assessment['assetData']['defaultInstanceChecks'][string] =>
      defaultChecksByGroup[groupId] || {},
  )((_: State, groupId: string) => groupId);

export const selectAseTemplateFieldFieldsConfig = createSelector(
  selectAseTemplateFields,
  (aseFieldConfig) => aseFieldConfig.fields,
);

const selectAseFieldInstanceActionIdsForGroupsByInstance = createSelector(
  selectAseFieldInstancesAse,
  selectAseActionsAse,
  (instances, actions) =>
    Object.values(actions).reduce<
      Record<string, { ids: string[]; idByInstance: Record<string, string[]> }>
    >((acc, action) => {
      if (action.fieldInstanceId) {
        const groupId = instances[action.fieldInstanceId]?.groupId;
        if (groupId) {
          if (!acc[groupId]) {
            acc[groupId] = {
              ids: [],
              idByInstance: {},
            };
          }
          acc[groupId]!.ids.push(action.id);
          if (!acc[groupId]!.idByInstance[action.fieldInstanceId]) {
            acc[groupId]!.idByInstance[action.fieldInstanceId] = [];
          }
          acc[groupId]!.idByInstance[action.fieldInstanceId]!.push(action.id);
        }
      }
      return acc;
    }, {}),
);

const selectAseFieldInstanceActionIdsForGroup = createCachedSelector(
  (_: State, { groupId }: SelectInstancesForGroup) => groupId,
  selectAseFieldInstanceActionIdsForGroupsByInstance,
  (_: State, { parent }: SelectInstancesForGroup) =>
    selectAseFieldInstanceChildInstanceIdsAstAse(_, parent),
  (groupId, actionIdsByGroup, instanceIds): string[] =>
    instanceIds !== null
      ? instanceIds.reduce<string[]>((acc, instanceId) => {
          const ids = actionIdsByGroup[groupId]?.idByInstance[instanceId];
          if (ids) {
            acc.push(...ids);
          }
          return acc;
        }, [])
      : actionIdsByGroup[groupId]?.ids || [],
)(forGroupCacheKeyFnc);

const selectAseFieldInstanceControlIdsForGroupsByInstance = createSelector(
  selectAseFieldInstancesAse,
  selectAseControlsAse,
  (instances, controls) =>
    Object.values(controls).reduce<
      Record<string, { ids: string[]; idByInstance: Record<string, string[]> }>
    >((acc, control) => {
      if (control.fieldInstanceId) {
        const groupId = instances[control.fieldInstanceId]?.groupId;
        if (groupId) {
          if (!acc[groupId]) {
            acc[groupId] = {
              ids: [],
              idByInstance: {},
            };
          }
          acc[groupId]!.ids.push(control.id);
          if (!acc[groupId]!.idByInstance[control.fieldInstanceId]) {
            acc[groupId]!.idByInstance[control.fieldInstanceId] = [];
          }
          acc[groupId]!.idByInstance[control.fieldInstanceId]!.push(control.id);
        }
      }
      return acc;
    }, {}),
);

const selectAseFieldInstanceControlIdsForGroup = createCachedSelector(
  (_: State, { groupId }: SelectInstancesForGroup) => groupId,
  selectAseFieldInstanceControlIdsForGroupsByInstance,
  (_: State, { parent }: SelectInstancesForGroup) =>
    selectAseFieldInstanceChildInstanceIdsAstAse(_, parent),
  (groupId, controlIdsByGroup, instanceIds): string[] =>
    instanceIds !== null
      ? instanceIds.reduce<string[]>((acc, instanceId) => {
          const ids = controlIdsByGroup[groupId]?.idByInstance[instanceId];
          if (ids) {
            acc.push(...ids);
          }
          return acc;
        }, [])
      : controlIdsByGroup[groupId]?.ids || [],
)(forGroupCacheKeyFnc);

export const selectAseResultsCount = createSelector(
  selectAseVisibleQuestionResults,
  (visibleQuestionResults) =>
    Object.values(visibleQuestionResults).reduce<{
      checklistsCount: number;
      completedChecklistsCount: number;
      questionsCount: number;
      completedQuestionsCount: number;
    }>(
      (acc, visibleQuestionResult) => {
        if (visibleQuestionResult.checklistItemIds.length) {
          acc.checklistsCount += 1;
          if (
            visibleQuestionResult.checklistItemIds.length ===
            visibleQuestionResult.checklistItemResults.length
          ) {
            acc.completedChecklistsCount += 1;
          }
        } else {
          acc.questionsCount += 1;
          if (visibleQuestionResult.answerId) {
            acc.completedQuestionsCount += 1;
          }
        }
        return acc;
      },
      {
        checklistsCount: 0,
        completedChecklistsCount: 0,
        questionsCount: 0,
        completedQuestionsCount: 0,
      },
    ),
);

const selectAseFieldInstanceResultsCountByGroup = createSelector(
  [
    selectAseIsEditable,
    selectAseTplQuestions,
    selectAseVisibleQuestionResults,
    selectAseTemplateFields,
    selectAseFieldInstancesAse,
    selectAseResultQuestions,
    selectAseResultChecklistItems,
  ],
  (
    isEditable,
    questions,
    visibleQuestionResults,
    aseFieldConfig,
    instances,
    resultQuestions,
    resultChecklistItems,
  ) => {
    const groups: Record<
      string,
      {
        checklistsCount: number;
        completedChecklistsCount: number;
        questionsCount: number;
        completedQuestionsCount: number;
        checklistsCountByInstance: Record<string, number>;
        completedChecklistsCountByInstance: Record<string, number>;
        questionsCountByInstance: Record<string, number>;
        completedQuestionsCountByInstance: Record<string, number>;
      }
    > = {};
    Object.values(instances).forEach(({ groupId, instanceId }) => {
      if (!groups[groupId]) {
        groups[groupId] = {
          checklistsCount: 0,
          completedChecklistsCount: 0,
          questionsCount: 0,
          completedQuestionsCount: 0,
          checklistsCountByInstance: {},
          completedChecklistsCountByInstance: {},
          questionsCountByInstance: {},
          completedQuestionsCountByInstance: {},
        };
      }
      (aseFieldConfig.groups[groupId]?.questionIds ?? []).forEach(
        (questionId) => {
          if (visibleQuestionResults[getAseResultKey(questionId, instanceId)]) {
            const question = questions[questionId];
            if (question?.type === AssessmentQuestionType.checklist) {
              groups[groupId]!.checklistsCount += 1;
              groups[groupId]!.checklistsCountByInstance[instanceId] =
                (groups[groupId]!.checklistsCountByInstance[instanceId] || 0) +
                1;

              const answeredChecklistsCount = question.checklistItemIds?.filter(
                (checklistItemId) =>
                  !!resultChecklistItems[
                    getAseResultKey(checklistItemId, instanceId)
                  ],
              ).length;

              if (
                isEditable
                  ? // All must be answered when editable
                    answeredChecklistsCount ===
                    question.checklistItemIds?.length
                  : // Just one must be answered when editable
                    !!answeredChecklistsCount
              ) {
                groups[groupId]!.completedChecklistsCount += 1;
                groups[groupId]!.completedChecklistsCountByInstance[
                  instanceId
                ] =
                  (groups[groupId]!.completedChecklistsCountByInstance[
                    instanceId
                  ] || 0) + 1;
              }
            } else {
              groups[groupId]!.questionsCount += 1;
              groups[groupId]!.questionsCountByInstance[instanceId] =
                (groups[groupId]!.questionsCountByInstance[instanceId] || 0) +
                1;
              if (
                resultQuestions[getAseResultKey(questionId, instanceId)]
                  ?.answerId
              ) {
                groups[groupId]!.completedQuestionsCount += 1;
                groups[groupId]!.completedQuestionsCountByInstance[instanceId] =
                  (groups[groupId]!.completedQuestionsCountByInstance[
                    instanceId
                  ] || 0) + 1;
              }
            }
          }
        },
      );
    });
    return groups;
  },
);

const defaultResultsCountForGroup: {
  checklistsCount: number;
  completedChecklistsCount: number;
  questionsCount: number;
  completedQuestionsCount: number;
} = {
  questionsCount: 0,
  completedQuestionsCount: 0,
  checklistsCount: 0,
  completedChecklistsCount: 0,
};

const selectAseFieldGroupResultsCountForGroup = createCachedSelector(
  selectAseFieldInstanceResultsCountByGroup,
  (_: State, { groupId }: SelectInstancesForGroup) => groupId,
  (_: State, { parent }: SelectInstancesForGroup) =>
    selectAseFieldInstanceChildInstanceIdsAstAse(_, parent),
  (resultsCount, groupId, instanceIds): typeof defaultResultsCountForGroup =>
    instanceIds !== null
      ? instanceIds.reduce<typeof defaultResultsCountForGroup>(
          (acc, instanceId) => {
            const groupCount = resultsCount[groupId];
            if (groupCount) {
              acc.questionsCount +=
                groupCount.questionsCountByInstance[instanceId] || 0;
              acc.completedQuestionsCount +=
                groupCount.completedQuestionsCountByInstance[instanceId] || 0;
              acc.checklistsCount +=
                groupCount.checklistsCountByInstance[instanceId] || 0;
              acc.completedChecklistsCount +=
                groupCount.completedChecklistsCountByInstance[instanceId] || 0;
            }
            return acc;
          },
          { ...defaultResultsCountForGroup },
        )
      : {
          questionsCount:
            resultsCount[groupId]?.questionsCount ||
            defaultResultsCountForGroup.questionsCount,
          completedQuestionsCount:
            resultsCount[groupId]?.completedQuestionsCount ||
            defaultResultsCountForGroup.completedQuestionsCount,
          checklistsCount:
            resultsCount[groupId]?.checklistsCount ||
            defaultResultsCountForGroup.checklistsCount,
          completedChecklistsCount:
            resultsCount[groupId]?.completedChecklistsCount ||
            defaultResultsCountForGroup.completedChecklistsCount,
        },
)(forGroupCacheKeyFnc);

export const selectAseFieldGroupStats = createCachedSelector(
  (_: State, { groupId }: SelectInstancesForGroup) =>
    selectAseTplFieldGroup(_, groupId),
  selectAseFieldInstanceIdsAstForGroup,
  selectAseFieldInstanceIdsForGroup,
  selectAseFieldInstanceActionIdsForGroup,
  selectAseFieldInstanceControlIdsForGroup,
  selectAseFieldGroupResultsCountForGroup,
  (_: State, { parent }: SelectInstancesForGroup) =>
    selectAseFieldInstanceChildInstanceIdsAstAse(_, parent),
  (
    group,
    assetInstanceIds,
    assessmentInstanceIds,
    actionIds,
    controlIds,
    resultsCount,
    childInstanceIds,
  ): {
    id: string;
    name: string;
    assetInstanceIds: string[];
    assessmentInstanceIds: string[];
    allInstancesCount: number;
    assetInstancesCount: number;
    assessmentInstancesCount: number;
    questionsCount: number;
    checklistsCount: number;
    completedQuestionsCount: number;
    completedChecklistsCount: number;
    actionsCount: number;
    controlsCount: number;
    childInstanceIds: string[];
    childInstancesCount: number;
  } => ({
    id: group!.id,
    name: group!.name,

    assetInstanceIds,
    assessmentInstanceIds,

    allInstancesCount: arrayUnique([
      ...assetInstanceIds,
      ...assessmentInstanceIds,
    ]).length,

    assetInstancesCount: assetInstanceIds.length,
    assessmentInstancesCount: assessmentInstanceIds.length,
    childInstanceIds: childInstanceIds || [],
    childInstancesCount: (childInstanceIds || []).length,

    questionsCount: resultsCount.questionsCount,
    checklistsCount: resultsCount.checklistsCount,

    completedQuestionsCount: resultsCount.completedQuestionsCount,
    completedChecklistsCount: resultsCount.completedChecklistsCount,

    actionsCount: actionIds.length,
    controlsCount: controlIds.length,
  }),
)(forGroupCacheKeyFnc);

export const selectAseFieldInstancePinsForGroup = createCachedSelector(
  (_: State, { groupId }: SelectInstancesForGroup) =>
    selectAseTplFieldGroup(_, groupId) || {
      disableAutoId: true,
      calculatedInfo: { pinFieldId: undefined },
    },
  selectAseFieldInstancesForGroup,
  (
    { disableAutoId, calculatedInfo: { pinFieldId } },
    instances,
  ): PinNewWithName[] =>
    !pinFieldId
      ? []
      : Object.values(instances).reduce<PinNewWithName[]>((acc, instance) => {
          const result = instance.results[pinFieldId];
          if (result?.pin) {
            // instance.
            acc.push({
              group: instance.inAssessment
                ? 'In Assessment'
                : 'Not In Assessment',
              color: instance.inAssessment
                ? theme.colors.blue
                : theme.colors.red,
              id: instance.instanceId,
              name: disableAutoId
                ? instance.reference || 'New'
                : instance.formattedAutoId!,
              planId: result.pin.planId,
              x: result.pin.x,
              y: result.pin.y,
            });
          }
          return acc;
        }, []),
)(forGroupCacheKeyFnc);

export const selectAseFieldInstanceQrsForGroups = createCachedSelector(
  (_: State, { groupId }: SelectInstancesForGroup) =>
    selectAseTplFieldGroup(_, groupId),
  selectAseFieldInstancesForGroup,
  (group, instances): string[] => {
    const qrFieldId = group?.calculatedInfo.qrFieldId;
    if (!qrFieldId) {
      return [];
    }
    return Object.values(instances).reduce<string[]>((acc, instance) => {
      const value = instance.results[qrFieldId]?.value;
      if (value) {
        acc.push(value as string);
      }
      return acc;
    }, []);
  },
)(forGroupCacheKeyFnc);

export const selectAseFieldInstanceLinkedItems = createCachedSelector(
  selectAseFieldInstanceAse,
  selectAseTemplateFieldGroupsConfig,
  selectAseTplQuestions,
  selectAseTplAnswers,
  selectAseResultQuestions,
  selectAseResultChecklistItems,
  selectAseActionsAse,
  selectAseControlsAse,
  (
    fieldInstance,
    groupsConfig,
    questions,
    answers,
    questionResults,
    checklistItemResults,
    actions,
    controls,
  ): {
    type:
      | AssessmentItemTypes.ACTION
      | AssessmentItemTypes.CONTROL
      | AssessmentItemTypes.QUESTION
      | AssessmentItemTypes.CHECKLISTITEM;
    id: string;
  }[] => {
    const itemsToBeDeleted: {
      type:
        | AssessmentItemTypes.ACTION
        | AssessmentItemTypes.CONTROL
        | AssessmentItemTypes.QUESTION
        | AssessmentItemTypes.CHECKLISTITEM;
      id: string;
    }[] = [];
    if (!fieldInstance) {
      return itemsToBeDeleted;
    }
    const { instanceId, groupId } = fieldInstance;
    const groupConfig = groupsConfig[groupId];
    if (!groupConfig) {
      return itemsToBeDeleted;
    }
    Object.values(actions).forEach((action) => {
      if (action.fieldInstanceId === instanceId) {
        itemsToBeDeleted.push({
          type: AssessmentItemTypes.ACTION,
          id: action.id,
        });
      }
    });
    Object.values(controls).forEach((control) => {
      if (control.fieldInstanceId === instanceId) {
        itemsToBeDeleted.push({
          type: AssessmentItemTypes.CONTROL,
          id: control.id,
        });
      }
    });
    const cascade = (thsQuestionId: string) => {
      const question = questions[thsQuestionId];
      const resultKey = getAseResultKey(thsQuestionId, instanceId);
      if (question?.type === AssessmentQuestionType.checklist) {
        question.checklistItemIds?.forEach((checklistItemId) => {
          const checklistResultKey = getAseResultKey(
            checklistItemId,
            instanceId,
          );
          if (checklistItemResults[checklistResultKey]) {
            itemsToBeDeleted.push({
              type: AssessmentItemTypes.CHECKLISTITEM,
              id: checklistResultKey,
            });
          }
        });
      } else if (questionResults[resultKey]?.answerId) {
        itemsToBeDeleted.push({
          type: AssessmentItemTypes.QUESTION,
          id: resultKey,
        });
        (question?.answer_ids || []).forEach((answerId) => {
          const answer = answers[answerId];
          (answer?.question_ids || []).forEach((thsSubQuestionId) => {
            cascade(thsSubQuestionId);
          });
        });
      }
    };
    groupConfig.questionIds.forEach((questionId) => cascade(questionId));
    return itemsToBeDeleted;
  },
)((_, fieldInstanceId) => fieldInstanceId);

export const selectAseFlagResults = createSelector(
  selectAseResults,
  (results) => results.flags,
);

export const selectAseFlagResult = createSelectorCached(
  [selectAseFlagResults, (_: State, flagId: string) => flagId],
  (flagResults, flagId) => flagResults[flagId],
);

const selectAseFlagHistory = createSelector(
  selectAseResults,
  (results) => results.flagHistory,
);

export const selectAseFlagHistoryForFlag = createSelectorCached(
  [selectAseFlagHistory, (_: State, flagId: string) => flagId],
  (flagHistory, flagId) => flagHistory.filter((f) => f.flagId === flagId),
);

export const selectAseSignatoryResults = createSelector(
  selectAseResults,
  (results) => results.signatories,
);
export const selectAseSignatoryResult = createSelectorCached(
  [selectAseSignatoryResults, (_: State, signatoryId: string) => signatoryId],
  (signatoryResults, signatoryId) => signatoryResults[signatoryId] || undefined,
);

/**
 * GENERIC
 */

export const selectAseShowSuggestionsUnderChecklistItems = createSelector(
  (state: State) =>
    selectGeneric(state, GenericKeyAseShowSuggestionsUnderChecklistItems),
  (showInline): boolean => !!showInline,
);
